import { Link, useParams, useSearchParams, useNavigate } from 'react-router';
import { motion as Motion } from 'motion/react';
import {
  Layers, Zap, AlertTriangle, RefreshCw,
  FolderKanban, ChevronDown, ChevronUp, FileCode, GitBranch, Copy, Check,
  TrendingDown, TrendingUp, Search, CheckCircle2,
  Pencil, X, Users, Trash2, Terminal, Settings,
} from 'lucide-react';
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Line, PieChart, Pie, Cell, Brush,
  BarChart, Bar,
} from 'recharts';
import { apiClient, apiPath } from '@/lib/api-client';
import { colors, accent, FADE } from '@/lib/tokens';
import { Spinner } from '@/components/ui/spinner';
import { Badge, METHOD_COLORS, DEFAULT_METHOD_COLOR, type BadgeColor } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Tooltip as UiTooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useAuth } from '@/lib/auth-context';
import { OnboardingSpotlight } from '@/components/onboarding/OnboardingSpotlight';
import { useOnboarding } from '@/components/onboarding/use-onboarding';
import { EmptyState } from '@/components/ui/empty-state';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { liveKeys, analyticsKeys, scanDeleteInvalidationPrefixes } from './project-detail-query-keys'
import { flattenPages, nextPageParam } from './list-pages';

// ─── Types ────────────────────────────────────────────────────────────────────

type FrequencyClass =
  | 'single' | 'conditional' | 'cache-guarded'
  | 'bounded-loop' | 'parallel' | 'unbounded-loop' | 'polling';

type CostModel = 'per_token' | 'per_transaction' | 'per_request' | 'free';
type EndpointStatus = 'normal' | 'redundant' | 'cacheable' | 'batchable' | 'n_plus_one_risk' | 'rate_limit_risk';
type Scope = 'internal' | 'external' | 'unknown';
type SuggestionType = 'redundancy' | 'cache' | 'n_plus_one' | 'batch' | 'rate_limit';
type SeverityLevel = 'high' | 'medium' | 'low';

interface CrossFileOrigin { file: string; functionName: string; }
interface CallSite {
  file: string; line: number; library?: string; frequency?: string;
  frequencyClass?: FrequencyClass; crossFileOrigin?: CrossFileOrigin;
}
interface MethodCostModel { model: CostModel; perCallCost?: number; }

interface EndpointRecord {
  id: string; projectId: string; scanId: string;
  provider: string; method: string; url: string;
  methodSignature?: string;
  files: string[]; callSites: CallSite[];
  callsPerDay: number; monthlyCost: number;
  status: EndpointStatus; scope?: Scope;
  frequencyClass?: FrequencyClass; costModel?: MethodCostModel;
  batchCapable?: boolean; cacheCapable?: boolean;
  streaming?: boolean; isMiddleware?: boolean;
  crossFileOrigins?: CrossFileOrigin[];
}

interface Suggestion {
  id: string; projectId: string; scanId: string;
  type: SuggestionType; severity: SeverityLevel;
  affectedEndpoints: string[]; affectedFiles: string[];
  estimatedMonthlySavings: number; description: string; codeFix: string;
}

interface ScanSummary {
  totalEndpoints: number; totalCallsPerDay: number;
  totalMonthlyCost: number; highRiskCount: number;
}

interface Scan {
  id: string; project_id?: string; projectId?: string;
  created_at?: string; createdAt?: string;
  summary: ScanSummary;
}

interface Project {
  id: string; name: string; description?: string;
  createdAt: string; updatedAt: string;
}

interface CostByProvider {
  provider: string; monthlyCost: number; callsPerDay: number; endpointCount: number;
}

interface CostByFile {
  file: string; monthlyCost: number; callsPerDay: number; endpointCount: number;
}

interface CostSummary {
  totalMonthlyCost: number; totalCallsPerDay: number; endpointCount: number;
}

interface AnalyticsPoint {
  timestamp: string;
  cost: number;
  requestCount: number;
}

interface AnalyticsDataRow {
  date?: string;
  windowStart?: string;
  totalCostCents: number;
  requestCount: number;
}

interface AnalyticsApiResponse {
  data: AnalyticsDataRow[];
}

function toAnalyticsPoints(rows: AnalyticsDataRow[]): AnalyticsPoint[] {
  const byKey = new Map<string, { cost: number; requestCount: number }>();
  for (const row of rows) {
    const key = row.date ?? row.windowStart ?? '';
    const existing = byKey.get(key) ?? { cost: 0, requestCount: 0 };
    existing.cost += row.totalCostCents / 100;
    existing.requestCount += row.requestCount;
    byKey.set(key, existing);
  }
  return Array.from(byKey.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([timestamp, { cost, requestCount }]) => ({ timestamp, cost, requestCount }));
}

interface TelemetryWindow {
  id: string;
  timestamp: string;
  endpoint: string;
  provider: string;
  model?: string;
  tokensIn?: number;
  tokensOut?: number;
  cost: number;
  latencyMs?: number;
  environment?: string;
}

interface TelemetryWindowRaw {
  windowId: string;
  projectId: string;
  environment: string;
  sdkLanguage: string;
  sdkVersion: string;
  windowStart: string;
  windowEnd: string;
  createdAt: string;
  metrics: {
    provider: string;
    endpoint: string;
    method: string;
    requestCount: number;
    errorCount: number;
    avgLatencyMs: number;
    p95LatencyMs: number;
    totalRequestBytes: number;
    totalResponseBytes: number;
    estimatedCostCents: number;
  }[];
}

function flattenTelemetryWindows(windows: TelemetryWindowRaw[]): TelemetryWindow[] {
  return windows.flatMap((w) =>
    w.metrics.map((m, i) => ({
      id: `${w.windowId}-${i}`,
      timestamp: w.windowStart,
      endpoint: m.endpoint,
      provider: m.provider,
      cost: m.estimatedCostCents / 100,
      latencyMs: m.avgLatencyMs,
      environment: w.environment,
    }))
  );
}

// ─── Styling constants ────────────────────────────────────────────────────────

// Re-export from Badge for local use
const METHOD_STYLES = METHOD_COLORS;
const DEFAULT_METHOD_STYLE = DEFAULT_METHOD_COLOR;

const FREQ_META: Record<FrequencyClass, { label: string } & BadgeColor> = {
  'unbounded-loop': { label: 'unbounded-loop', bg: colors.errorSubtle,   border: colors.errorBorder,   color: colors.error   },
  'polling':        { label: 'polling',         bg: colors.warningSubtle, border: colors.warningBorder, color: colors.warning },
  'parallel':       { label: 'parallel',        bg: colors.warningSubtle, border: colors.warningBorder, color: colors.warning },
  'bounded-loop':   { label: 'bounded-loop',    bg: colors.accentSubtle,  border: colors.accentBorder,  color: accent         },
  'conditional':    { label: 'conditional',     bg: 'transparent',        border: colors.borderDefault, color: colors.textSecondary },
  'single':         { label: 'single',          bg: 'transparent',        border: colors.borderDefault, color: colors.textMuted     },
  'cache-guarded':  { label: 'cache-guarded',   bg: 'rgba(34,197,94,0.07)', border: 'rgba(34,197,94,0.20)', color: '#4ade80' },
};

const STATUS_META: Partial<Record<EndpointStatus, { label: string } & BadgeColor>> = {
  'rate_limit_risk': { label: 'rate limit',  bg: colors.errorSubtle,   border: colors.errorBorder,   color: colors.error },
  'n_plus_one_risk': { label: 'N+1 risk',    bg: colors.errorSubtle,   border: colors.errorBorder,   color: colors.error },
  'cacheable':       { label: 'cacheable',   bg: 'rgba(34,197,94,0.07)', border: 'rgba(34,197,94,0.20)', color: '#4ade80' },
  'batchable':       { label: 'batchable',   bg: colors.accentSubtle,  border: colors.accentBorder,  color: accent },
  'redundant':       { label: 'redundant',   bg: colors.warningSubtle, border: colors.warningBorder, color: colors.warning },
};

const COST_MODEL_META: Record<CostModel, { label: string; color: string }> = {
  per_token:       { label: 'token',  color: accent },
  per_transaction: { label: 'txn',    color: accent },
  per_request:     { label: 'req',    color: colors.textMuted },
  free:            { label: 'free',   color: '#4ade80' },
};

const SUGGESTION_TYPE_LABELS: Record<SuggestionType, string> = {
  n_plus_one: 'N+1',
  rate_limit: 'rate limit',
  cache:      'cache',
  batch:      'batch',
  redundancy: 'redundant',
};

const ONBOARDING_TOUR_STEPS = [
  {
    selector: '[data-onboarding-target="project-header"]',
    tab: 'static',
    stepLabel: 'Step 1 of 8',
    title: 'We already prepared your first project',
    body: 'This is the workspace anchor for the project you are stepping into first. As Recost fills in, this page becomes the place you return to for the full picture.',
  },
  {
    selector: '[data-onboarding-target="project-tabs"]',
    tab: 'static',
    stepLabel: 'Step 2 of 8',
    title: 'Each tab answers a different cost question',
    body: 'Static is for scan output and inventory, Live is for what is happening right now, and Analytics is where longer-term cost patterns become obvious.',
  },
  {
    selector: '[data-onboarding-target="static-overview"]',
    tab: 'static',
    stepLabel: 'Step 3 of 8',
    title: 'This is where cost and usage patterns surface first',
    body: 'Endpoint inventory, provider usage, monthly cost, and optimization opportunities all start to light up here once your scans are connected.',
    noOverlay: true,
  },
  {
    selector: '[data-onboarding-target="scanned-files"]',
    tab: 'static',
    stepLabel: 'Step 4 of 8',
    title: 'Scanned files shows what the scan actually covered',
    body: 'Use this to verify the files and directories Recost pulled in, so you know whether the inventory is based on the right part of your codebase.',
  },
  {
    selector: '[data-onboarding-target="scan-history"]',
    tab: 'static',
    stepLabel: 'Step 5 of 8',
    title: 'Scan history helps you compare how the project changes',
    body: 'As you run more scans, this becomes your quick timeline for when costs, endpoints, and risks shifted.',
  },
  {
    selector: '[data-onboarding-target="live-overview"]',
    tab: 'live',
    stepLabel: 'Step 6 of 8',
    title: 'Live is for current provider activity',
    body: 'This page is built for recent traffic, request volume, and provider cost movement when you want to understand what is happening now.',
    noOverlay: true,
  },
  {
    selector: '[data-onboarding-target="analytics-overview"]',
    tab: 'analytics',
    stepLabel: 'Step 7 of 8',
    title: 'Analytics is for the longer trend line',
    body: 'Use Analytics when you want to understand how costs, requests, and model usage evolve over time instead of only reading one snapshot.',
    noOverlay: true,
  },
  {
    selector: '[data-onboarding-target="settings-quick-start"]',
    tab: 'settings',
    stepLabel: 'Step 8 of 8',
    title: 'Next step: connect your API key and scanner',
    body: 'Generate an API key, connect the scanner, and run the first scan. That is what turns this prepared project into a live operating surface.',
  },
] as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(dateStr?: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtCost(n: number) {
  if (n === 0) return '$0';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtNum(n: number) {
  return n.toLocaleString('en-US');
}

function shortFile(f: string) {
  const parts = f.split('/');
  return parts.length > 2 ? `…/${parts.slice(-2).join('/')}` : f;
}

// ─── Micro-components ────────────────────────────────────────────────────────

function Chip({ color: c, children }: { color: string; children: React.ReactNode }) {
  return (
    <Badge color={{ bg: 'transparent', border: c, color: c }} className="text-[10px] opacity-85">
      {children}
    </Badge>
  );
}

function CenteredSpinner() {
  return (
    <div className="flex items-center justify-center py-16">
      <Spinner />
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      className="flex flex-col items-center justify-center py-20 text-center rounded-lg"
      style={{ background: colors.errorSubtle, border: `1px solid ${colors.errorBorder}` }}
    >
      <AlertTriangle className="w-6 h-6 mb-3" style={{ color: colors.error }} />
      <p className="text-sm font-semibold mb-1" style={{ color: colors.textPrimary }}>{message}</p>
      <p className="text-sm mb-5" style={{ color: colors.textMuted }}>Check your connection and try again.</p>
      <button
        onClick={onRetry}
        className="flex items-center gap-2 px-4 py-2 rounded-md text-sm transition-all cursor-pointer"
        style={{ background: colors.errorSubtle, border: `1px solid ${colors.errorBorder}`, color: colors.error }}
      >
        <RefreshCw className="w-3.5 h-3.5" />
        Retry
      </button>
    </div>
  );
}

// ─── Endpoint row ─────────────────────────────────────────────────────────────

function EndpointRow({ ep, suggestion }: { ep: EndpointRecord; suggestion?: Suggestion }) {
  const methodStyle = METHOD_STYLES[ep.method] ?? DEFAULT_METHOD_STYLE;
  const statusMeta = ep.status !== 'normal' ? STATUS_META[ep.status] : undefined;
  const freqMeta = ep.frequencyClass ? FREQ_META[ep.frequencyClass] : undefined;
  const costModelMeta = ep.costModel ? COST_MODEL_META[ep.costModel.model] : undefined;

  const hasCost = ep.monthlyCost > 0;

  const chips: { label: string; color: string }[] = [
    ...(ep.isMiddleware  ? [{ label: 'middleware', color: colors.warning }]     : []),
    ...(ep.streaming     ? [{ label: 'streaming',  color: '#38bdf8' }]          : []),
    ...(ep.batchCapable  ? [{ label: 'batchable',  color: accent }]             : []),
    ...(ep.cacheCapable  ? [{ label: 'cacheable',  color: '#4ade80' }]          : []),
  ];

  const crossOriginCount = ep.crossFileOrigins?.length ?? 0;

  return (
    <div
      className="px-3 py-3 sm:px-5 sm:py-3.5 transition-colors"
      style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}
    >
      {/* Top row */}
      <div className="flex items-start gap-2 sm:gap-3 min-w-0">
        {/* Method badge */}
        <Badge color={methodStyle} className="flex-shrink-0 justify-center w-[46px] text-[10px]">
          {ep.method}
        </Badge>

        {/* URL + metadata */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {/* Provider */}
            <span className="text-xs font-medium" style={{ color: colors.textMuted }}>{ep.provider}</span>
            {/* Scope (only show external) */}
            {ep.scope === 'external' && (
              <span className="text-[10px] font-mono" style={{ color: colors.textMuted }}>ext</span>
            )}
            {/* URL */}
            <span
              className="text-xs font-mono truncate max-w-[280px] sm:max-w-none"
              style={{ color: colors.textPrimary }}
              title={ep.url}
            >
              {ep.url.replace(/^https?:\/\/[^/]+/, '')}
            </span>
          </div>

          {/* Method signature */}
          {ep.methodSignature && (
            <p className="text-[11px] font-mono mt-0.5" style={{ color: colors.textMuted }}>
              {ep.methodSignature}
            </p>
          )}

          {/* Files row */}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1">
            {ep.files.slice(0, 2).map((f) => (
              <span key={f} className="text-[10px] font-mono" style={{ color: colors.textMuted }}>
                {shortFile(f)}
              </span>
            ))}
            {ep.files.length > 2 && (
              <span className="text-[10px] font-mono" style={{ color: colors.textMuted }}>
                +{ep.files.length - 2} more
              </span>
            )}
            {crossOriginCount > 0 && (
              <span className="flex items-center gap-0.5 text-[10px] font-mono" style={{ color: colors.textMuted }}>
                <GitBranch className="w-2.5 h-2.5" />
                {crossOriginCount} origin{crossOriginCount > 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {/* Right side: badges + cost */}
        <div className="flex-shrink-0 flex flex-col items-end gap-1.5 ml-auto pl-2">
          {/* Cost */}
          <span
            className="text-sm font-mono font-semibold tabular-nums"
            style={{ color: hasCost ? accent : colors.textMuted }}
          >
            {fmtCost(ep.monthlyCost)}<span className="text-[10px] font-normal">/mo</span>
          </span>
          <span className="text-[10px] font-mono" style={{ color: colors.textMuted }}>
            {fmtNum(ep.callsPerDay)}/day
          </span>
        </div>
      </div>

      {/* Badge row */}
      {(freqMeta || statusMeta || costModelMeta || chips.length > 0) && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2 ml-[58px] sm:ml-[62px]">
          {freqMeta && (
            <Badge color={freqMeta} className="text-[10px]">
              {freqMeta.label}
            </Badge>
          )}
          {statusMeta && (
            <Badge color={statusMeta} className="text-[10px]">
              {statusMeta.label}
            </Badge>
          )}
          {costModelMeta && (
            <Chip color={costModelMeta.color}>{costModelMeta.label}</Chip>
          )}
          {chips.map((c) => (
            <Chip key={c.label} color={c.color}>{c.label}</Chip>
          ))}
        </div>
      )}

      {/* Inline suggestion hint */}
      {suggestion && (
        <div
          className="flex items-center justify-between ml-[58px] sm:ml-[62px] mt-2 px-2 py-1 rounded"
          style={{ background: colors.accentSubtle, border: `1px solid ${colors.accentBorder}` }}
        >
          <div className="flex items-center gap-1.5">
            <TrendingDown className="w-3 h-3" style={{ color: accent }} />
            <span className="text-[10px] font-mono" style={{ color: accent }}>
              {SUGGESTION_TYPE_LABELS[suggestion.type]}
            </span>
          </div>
          {suggestion.estimatedMonthlySavings > 0 && (
            <span className="text-[10px] font-mono font-semibold" style={{ color: accent }}>
              −{fmtCost(suggestion.estimatedMonthlySavings)}/mo
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Provider colour map ──────────────────────────────────────────────────────

const PROVIDER_COLORS: Record<string, string> = {
  openai:      '#F26B1D',
  anthropic:   '#3b82f6',
  stripe:      '#6366f1',
  cohere:      '#8b5cf6',
  twilio:      '#0891b2',
  sendgrid:    '#0891b2',
  huggingface: '#eab308',
  pinecone:    '#22c55e',
  'aws s3':    '#f97316',
  'aws sqs':   '#f97316',
  github:      '#a3a3a3',
};

// ─── Provider group (collapsible, for static tab) ────────────────────────────

function ProviderGroup({
  provider,
  endpoints,
  totalCost,
  grandTotal,
  suggestions,
}: {
  provider: string;
  endpoints: EndpointRecord[];
  totalCost: number;
  grandTotal: number;
  suggestions: Suggestion[];
}) {
  const [expanded, setExpanded] = useState(false);
  const [hovered, setHovered] = useState(false);
  const color = PROVIDER_COLORS[provider.toLowerCase()] ?? colors.textSecondary;
  const costShare = grandTotal > 0 ? (totalCost / grandTotal) * 100 : 0;

  // Map endpoint id → highest-savings suggestion (first match wins)
  const suggestionByEndpoint = new Map<string, Suggestion>();
  for (const sg of suggestions) {
    for (const epId of sg.affectedEndpoints) {
      if (!suggestionByEndpoint.has(epId)) {
        suggestionByEndpoint.set(epId, sg);
      }
    }
  }

  return (
    <div style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}>
      <button
        className="relative w-full flex items-center gap-4 px-5 py-4 sm:px-6 cursor-pointer text-left overflow-hidden focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)]"
        style={{ background: 'transparent' }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => setExpanded((v) => !v)}
      >
        {/* Proportional cost fill — full-width background */}
        <div
          className="absolute inset-y-0 left-0 pointer-events-none transition-all duration-700"
          style={{ width: `${costShare}%`, background: color, opacity: hovered ? 0.1 : 0.06 }}
        />
        {/* Hover wash for unfilled area */}
        {hovered && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: colors.bgHover, opacity: 0.6 }}
          />
        )}

        {/* Content — relative so it sits above background fills */}
        <span className="relative w-2 h-2 rounded-sm flex-shrink-0" style={{ background: color }} />
        <span className="relative text-sm font-medium flex-1 min-w-0 text-left" style={{ color: colors.textPrimary }}>
          {provider}
        </span>
        <span className="relative text-xs font-mono flex-shrink-0" style={{ color: colors.textMuted }}>
          {endpoints.length} endpoint{endpoints.length !== 1 ? 's' : ''}
        </span>
        <span
          className="relative text-sm font-mono font-semibold tabular-nums ml-4"
          style={{ color: totalCost > 0 ? accent : colors.textMuted }}
        >
          {totalCost > 0 ? fmtCost(totalCost) : 'free'}
        </span>
        <ChevronDown
          className="relative w-3.5 h-3.5 flex-shrink-0 transition-transform"
          style={{
            color: colors.textMuted,
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>

      {expanded && (
        <div style={{ background: colors.bgSubtle, borderTop: `1px solid ${colors.borderSubtle}` }}>
          {endpoints.map((ep) => (
            <EndpointRow key={ep.id} ep={ep} suggestion={suggestionByEndpoint.get(ep.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Scanned files (collapsible, for static tab) ──────────────────────────────

function ScannedFiles({ endpoints }: { endpoints: EndpointRecord[] }) {
  const [expanded, setExpanded] = useState(false);

  // Collect unique files from endpoint.files and endpoint.callSites[].file
  const fileSet = new Set<string>();
  for (const ep of endpoints) {
    for (const f of ep.files) fileSet.add(f);
    for (const cs of ep.callSites) fileSet.add(cs.file);
  }

  // Group by directory
  const byDir = new Map<string, string[]>();
  for (const f of fileSet) {
    const parts = f.replace(/\\/g, '/').split('/');
    const dir = parts.length > 1 ? parts.slice(0, -1).join('/') : '.';
    if (!byDir.has(dir)) byDir.set(dir, []);
    byDir.get(dir)!.push(f);
  }

  const totalFiles = fileSet.size;
  const totalDirs = byDir.size;

  if (totalFiles === 0) return null;

  return (
    <Motion.div {...FADE(0)} className="mb-8" data-onboarding-target="scanned-files">
      <button
        className="flex items-center gap-2 mb-4 cursor-pointer focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)] rounded"
        style={{ background: 'none', border: 'none', padding: 0 }}
        onClick={() => setExpanded((v) => !v)}
      >
        <FileCode className="w-3.5 h-3.5" style={{ color: colors.textMuted }} />
        <p className="text-xs uppercase tracking-[0.12em]" style={{ color: colors.textMuted }}>
          Scanned files
        </p>
        <span className="text-[10px] font-mono" style={{ color: colors.textMuted }}>
          {totalFiles} file{totalFiles !== 1 ? 's' : ''} · {totalDirs} dir{totalDirs !== 1 ? 's' : ''}
        </span>
        {expanded
          ? <ChevronUp className="w-3.5 h-3.5" style={{ color: colors.textMuted }} />
          : <ChevronDown className="w-3.5 h-3.5" style={{ color: colors.textMuted }} />}
      </button>

      {expanded && (
        <Card className="overflow-hidden">
          {[...byDir.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([dir, files], di, arr) => (
            <div key={dir} style={{ borderBottom: di < arr.length - 1 ? `1px solid ${colors.borderSubtle}` : undefined }}>
              <div
                className="flex items-center gap-1.5 px-4 py-1.5 sm:px-5"
                style={{ background: colors.bgHover }}
              >
                <FolderKanban className="w-3 h-3 flex-shrink-0" style={{ color: colors.textMuted }} />
                <span className="text-[10px] font-mono" style={{ color: colors.textMuted }}>
                  {dir}/
                </span>
              </div>
              {[...files].sort().map((f) => {
                const filename = f.replace(/\\/g, '/').split('/').pop() ?? f;
                return (
                  <div
                    key={f}
                    className="flex items-center gap-2 px-4 py-1.5 sm:px-5"
                    style={{ borderTop: `1px solid ${colors.borderSubtle}` }}
                  >
                    <FileCode className="w-3 h-3 flex-shrink-0" style={{ color: colors.textMuted }} />
                    <span className="text-[11px] font-mono" style={{ color: colors.textSecondary }}>{filename}</span>
                    <span className="text-[10px] font-mono truncate" style={{ color: colors.textMuted }}>{f}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </Card>
      )}
    </Motion.div>
  );
}

// ─── Sort dropdown ────────────────────────────────────────────────────────────

function SortDropdown<T extends string>({
  value,
  options,
  onChange,
  width = 'w-[104px]',
}: {
  value: T;
  options: Record<T, string>;
  onChange: (v: T) => void;
  width?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  return (
    <div ref={ref} className={`relative ${width}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-2 py-1 rounded text-xs cursor-pointer transition-colors focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)]"
        style={{
          background: open ? colors.bgHover : colors.bgSubtle,
          border: `1px solid ${open ? colors.borderHover : colors.borderDefault}`,
          color: colors.textSecondary,
        }}
        onMouseEnter={(e) => {
          if (!open) (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderHover;
        }}
        onMouseLeave={(e) => {
          if (!open) (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderDefault;
        }}
      >
        <span>{options[value]}</span>
        <ChevronDown
          className="w-3 h-3 flex-shrink-0 transition-transform"
          style={{ color: colors.textMuted, transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 rounded overflow-hidden z-20 w-full"
          style={{
            background: colors.bgBase,
            border: `1px solid ${colors.borderDefault}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          }}
        >
          {(Object.keys(options) as T[]).map((k) => {
            const isActive = k === value;
            return (
              <button
                key={k}
                onClick={() => { onChange(k); setOpen(false); }}
                className="w-full text-left px-2 py-1.5 text-xs cursor-pointer transition-colors"
                style={{
                  background: isActive ? colors.bgHover : 'transparent',
                  color: isActive ? colors.textPrimary : colors.textSecondary,
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = colors.bgHover; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = isActive ? colors.bgHover : 'transparent'; }}
              >
                {options[k]}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Live tab ─────────────────────────────────────────────────────────────────

type EndpointSort = 'cost' | 'calls' | 'provider';
type TimeRange = '24h' | '7d' | '30d';

const SORT_LABELS: Record<EndpointSort, string> = {
  cost:     'By cost',
  calls:    'By calls',
  provider: 'By provider',
};

function fmtAnalyticsLabel(ts: string, range: TimeRange) {
  const d = new Date(ts);
  if (range === '24h') return `${String(d.getHours()).padStart(2, '0')}:00`;
  if (range === '7d') return ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()];
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function fmtTimestamp(ts: string) {
  return new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function LiveTab({ projectId }: { projectId: string }) {
  const [timeRange, setTimeRange] = useState<TimeRange>('30d');
  const [environment, setEnvironment] = useState('all');
  const [endpointSort, setEndpointSort] = useState<EndpointSort>('cost');
  const [requestSearch, setRequestSearch] = useState('');
  const [requestProviderFilter, setRequestProviderFilter] = useState('All');
  const [visibleRequests, setVisibleRequests] = useState(10);
  const [copiedSnippet, setCopiedSnippet] = useState<'node' | 'python' | null>(null);
  const [snippetTab, setSnippetTab] = useState<'node' | 'python'>('node');
  const [secsAgo, setSecsAgo] = useState(0);
  const [livePoints, setLivePoints] = useState<{ t: string; calls: number }[]>([]);
  const liveBaseRef = useRef<number>(0);

  // ── Date range helpers ───────────────────────────────────────────────────
  function getRange(range: TimeRange, offset = 0) {
    const days = range === '24h' ? 1 : range === '7d' ? 7 : 30;
    const to = new Date(Date.now() - offset * days * 86_400_000);
    const from = new Date(to.getTime() - days * 86_400_000);
    return { from: from.toISOString(), to: to.toISOString(), interval: range === '24h' ? 'window' : 'day' };
  }

  const envParam = environment !== 'all' ? `&environment=${environment}` : '';
  const { from, to, interval } = getRange(timeRange);
  const { from: prevFrom, to: prevTo } = getRange(timeRange, 1);

  // ── Safe fetch wrapper (shows toast on 429) ──────────────────────────────
  const safeFetch = useCallback(<T,>(path: string) =>
    apiClient.get<T>(path).catch((err: Error) => {
      if (err.message?.includes('429')) toast.error('Too many requests — please wait a moment');
      throw err;
    }), []);

  // ── Empty-state check ────────────────────────────────────────────────────
  // Checks both raw windows and rolled-up daily rows — the rollup service prunes
  // telemetry_windows after 7 days but keeps telemetry_daily, so projects with
  // only rollup data would otherwise look empty here.
  const { data: healthCheck, isLoading: checkLoading } = useQuery<{ hasAny: boolean }>({
    queryKey: liveKeys.check(projectId),
    queryFn: () => safeFetch<{ data: { hasAny: boolean } }>(apiPath`/projects/${projectId}/telemetry/health`).then(r => r.data),
    staleTime: 60_000,
  });

  const hasData = healthCheck?.hasAny === true;

  // ── Analytics — current period ───────────────────────────────────────────
  const {
    data: analyticsData,
    isLoading: analyticsLoading,
    isError: analyticsError,
    dataUpdatedAt,
  } = useQuery<AnalyticsPoint[]>({
    queryKey: liveKeys.analytics(projectId, timeRange, environment),
    queryFn: () => safeFetch<{ data: AnalyticsApiResponse }>(apiPath`/projects/${projectId}/analytics` + `?from=${from}&to=${to}&interval=${interval}${envParam}`).then(r => toAnalyticsPoints(r.data.data)),
    enabled: hasData,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  // ── Analytics — previous period (for % change) ───────────────────────────
  const { data: prevAnalyticsData } = useQuery<AnalyticsPoint[]>({
    queryKey: liveKeys.analyticsPrev(projectId, timeRange, environment),
    queryFn: () => safeFetch<{ data: AnalyticsApiResponse }>(apiPath`/projects/${projectId}/analytics` + `?from=${prevFrom}&to=${prevTo}&interval=${interval}${envParam}`).then(r => toAnalyticsPoints(r.data.data)),
    enabled: hasData,
  });

  // ── Cost by provider ─────────────────────────────────────────────────────
  const {
    data: costByProvider = [],
  } = useQuery<CostByProvider[]>({
    queryKey: liveKeys.costByProvider(projectId, environment),
    queryFn: () => safeFetch<{ data: CostByProvider[] }>(apiPath`/projects/${projectId}/cost/by-provider` + (envParam ? `?${envParam.slice(1)}` : '')).then(r => r.data),
    enabled: hasData,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  // ── Endpoints ────────────────────────────────────────────────────────────
  const {
    data: endpoints = [],
    isLoading: endpointsLoading,
    isError: endpointsError,
    refetch: refetchEndpoints,
  } = useQuery<EndpointRecord[]>({
    queryKey: liveKeys.endpoints(projectId),
    queryFn: () => safeFetch<{ data: EndpointRecord[] }>(apiPath`/projects/${projectId}/endpoints` + `?limit=100`).then(r => r.data),
    enabled: hasData,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  // ── Telemetry (recent requests) ──────────────────────────────────────────
  const { data: telemetryData = [] } = useQuery<TelemetryWindow[]>({
    queryKey: liveKeys.telemetry(projectId, environment),
    queryFn: () => safeFetch<{ data: TelemetryWindowRaw[] }>(apiPath`/projects/${projectId}/telemetry/recent` + `?limit=50${envParam}`).then(r => flattenTelemetryWindows(r.data)),
    enabled: hasData,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  // ── Last-updated ticker ──────────────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => setSecsAgo(Math.floor((Date.now() - dataUpdatedAt) / 1000)), 5_000);
    return () => clearInterval(id);
  }, [dataUpdatedAt]);

  // ── Derived values needed before early returns ───────────────────────────
  const _totalRequests = analyticsData?.reduce((s, p) => s + p.requestCount, 0) ?? 0;
  const _numDays       = timeRange === '24h' ? 1 : timeRange === '7d' ? 7 : 30;
  const callsPerDay    = _numDays > 0 ? _totalRequests / _numDays : 0;

  // ── Live rolling chart — seed from analytics, then tick every 3s ─────────
  useEffect(() => {
    if (callsPerDay <= 0) return;
    liveBaseRef.current = callsPerDay / (24 * 12);
    const now = Date.now();
    const seed = Array.from({ length: 20 }, (_, i) => {
      const jitter = 0.6 + Math.random() * 0.8;
      return {
        t: new Date(now - (19 - i) * 5 * 60_000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        calls: Math.max(0, Math.round(liveBaseRef.current * jitter)),
      };
    });
    setLivePoints(seed);
  }, [callsPerDay]);

  useEffect(() => {
    const id = setInterval(() => {
      if (liveBaseRef.current <= 0) return;
      const jitter = 0.5 + Math.random() * 1.0;
      const newPoint = {
        t: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        calls: Math.max(0, Math.round(liveBaseRef.current * jitter)),
      };
      setLivePoints(prev => [...prev.slice(-29), newPoint]);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  // ── Memos (MUST be declared before any early return — React hook order) ──
  const costSeries = useMemo(
    () => analyticsData?.map(p => ({ label: fmtAnalyticsLabel(p.timestamp, timeRange), cost: p.cost })) ?? [],
    [analyticsData, timeRange],
  );
  const volSeries = useMemo(
    () => analyticsData?.map(p => ({ label: fmtAnalyticsLabel(p.timestamp, timeRange), calls: p.requestCount })) ?? [],
    [analyticsData, timeRange],
  );
  const sortedEndpoints = useMemo(
    () => [...endpoints].sort((a, b) => {
      if (endpointSort === 'calls')    return b.callsPerDay - a.callsPerDay;
      if (endpointSort === 'provider') return a.provider.localeCompare(b.provider);
      return b.monthlyCost !== a.monthlyCost ? b.monthlyCost - a.monthlyCost : b.callsPerDay - a.callsPerDay;
    }),
    [endpoints, endpointSort],
  );
  const allProviders = useMemo(
    () => ['All', ...Array.from(new Set(telemetryData.map(r => r.provider)))],
    [telemetryData],
  );

  // ── Loading / empty states ───────────────────────────────────────────────
  if (checkLoading) return <CenteredSpinner />;

  if (!hasData) {
    const nodeSnippet = `import { init } from '@recost-dev/node';

init({
  apiKey:    process.env.RECOST_API_KEY,
  projectId: '${projectId}',
});`;

    const pythonSnippet = `from recost import init, RecostConfig

init(RecostConfig(
    api_key="<YOUR_API_KEY>",
    project_id="${projectId}",
))`;

    const snippet = snippetTab === 'node' ? nodeSnippet : pythonSnippet;

    function copySnippet() {
      navigator.clipboard.writeText(snippet).then(() => {
        setCopiedSnippet(snippetTab);
        setTimeout(() => setCopiedSnippet(null), 1800);
      });
    }

    return (
      <Motion.div {...FADE(0.1)} className="flex flex-col items-center justify-center py-20 text-center">
        <Card
          className="w-12 h-12 flex items-center justify-center mb-5"
        >
          <Zap className="w-5 h-5" style={{ color: colors.textMuted }} />
        </Card>
        <h3 className="text-lg font-bold mb-2" style={{ color: colors.textPrimary }}>No live data yet</h3>
        <p className="text-sm mb-8" style={{ color: colors.textMuted }}>
          Connect the Recost SDK to start tracking your API costs in real-time
        </p>

        <Card
          className="w-full overflow-hidden text-left"
        >
          <div className="flex items-center" style={{ borderBottom: `1px solid ${colors.borderDefault}` }}>
            {(['node', 'python'] as const).map((lang) => (
              <button
                key={lang}
                onClick={() => setSnippetTab(lang)}
                className="relative px-4 pb-2.5 pt-2.5 text-xs font-mono transition-colors cursor-pointer"
                style={{ color: snippetTab === lang ? colors.textPrimary : colors.textMuted }}
              >
                {lang === 'node' ? 'Node.js' : 'Python'}
                {snippetTab === lang && (
                  <span className="absolute inset-x-0 -bottom-px h-[2px]" style={{ background: accent }} />
                )}
              </button>
            ))}
            <button
              onClick={copySnippet}
              aria-label={copiedSnippet === snippetTab ? 'Copied' : 'Copy snippet'}
              className="ml-auto flex items-center gap-1.5 px-3 py-2 text-xs cursor-pointer transition-colors focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)] rounded"
              style={{ color: copiedSnippet === snippetTab ? '#4ade80' : colors.textMuted }}
            >
              {copiedSnippet === snippetTab ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
              {copiedSnippet === snippetTab ? 'Copied' : 'Copy'}
            </button>
          </div>
          <pre
            className="px-5 py-4 text-[11px] font-mono leading-relaxed overflow-x-auto"
            style={{ background: colors.bgSubtle, color: colors.textSecondary, margin: 0 }}
          >
            {snippet}
          </pre>
        </Card>

        <p className="text-xs mt-5" style={{ color: colors.textMuted }}>
          Data will appear here automatically once your first API call is tracked
        </p>
      </Motion.div>
    );
  }

  // ── Data derivations ─────────────────────────────────────────────────────
  const totalCost     = analyticsData?.reduce((s, p) => s + p.cost, 0) ?? 0;
  const totalRequests = _totalRequests;
  const numDays       = _numDays;
  const avgCostPerRequest = totalRequests > 0 ? totalCost / totalRequests : 0;
  const endpointCount = endpoints.length;

  const prevTotalCost   = prevAnalyticsData?.reduce((s, p) => s + p.cost, 0) ?? 0;
  const prevTotalReqs   = prevAnalyticsData?.reduce((s, p) => s + p.requestCount, 0) ?? 0;
  const prevCallsPerDay = numDays > 0 ? prevTotalReqs / numDays : 0;
  const costChangePct   = prevTotalCost > 0 ? ((totalCost - prevTotalCost) / prevTotalCost) * 100 : null;
  const callsChangePct  = prevCallsPerDay > 0 ? ((callsPerDay - prevCallsPerDay) / prevCallsPerDay) * 100 : null;

  const maxProvCost = Math.max(...costByProvider.map((p) => p.monthlyCost), 1);
  const topEndpoints = sortedEndpoints.slice(0, 8);
  const filteredRequests = telemetryData.filter((r) => {
    const matchesSearch = requestSearch === '' || r.endpoint.includes(requestSearch) || (r.model ?? '').toLowerCase().includes(requestSearch.toLowerCase());
    const matchesProvider = requestProviderFilter === 'All' || r.provider === requestProviderFilter;
    return matchesSearch && matchesProvider;
  });

  const timeRangeLabel = timeRange === '24h' ? '24 hours' : timeRange === '7d' ? '7 days' : '30 days';
  const lastUpdatedLabel = dataUpdatedAt === 0 ? '' : secsAgo < 60 ? `${secsAgo}s ago` : `${Math.floor(secsAgo / 60)}m ago`;

  return (
    <Motion.div {...FADE(0.1)} className="space-y-5" data-onboarding-target="live-overview">

      {/* KPI row */}
      <Card
        className="grid grid-cols-2 sm:grid-cols-4 overflow-hidden"
      >
        {[
          {
            label: 'Monthly Cost',
            value: analyticsLoading ? '…' : fmtCost(totalCost),
            color: totalCost > 0 ? accent : colors.textPrimary,
            change: costChangePct,
          },
          {
            label: 'Calls / Day',
            value: analyticsLoading ? '…' : fmtNum(Math.round(callsPerDay)),
            color: colors.textPrimary,
            change: callsChangePct,
          },
          {
            label: 'Endpoints',
            value: endpointsLoading ? '…' : String(endpointCount),
            color: colors.textPrimary,
            change: null,
          },
          {
            label: 'Avg Cost / Req',
            value: analyticsLoading ? '…' : `$${avgCostPerRequest.toFixed(4)}`,
            color: avgCostPerRequest > 0 ? accent : colors.textPrimary,
            change: null,
          },
        ].map(({ label, value, color, change }, i) => (
          <div
            key={label}
            className="px-4 py-3 sm:px-5 sm:py-4"
            style={{ borderRight: i < 3 ? `1px solid ${colors.borderSubtle}` : undefined }}
          >
            <p className="text-[10px] uppercase tracking-[0.08em] mb-1" style={{ color: colors.textMuted }}>{label}</p>
            <p className="text-base sm:text-lg font-bold font-mono tabular-nums" style={{ color }}>{value}</p>
            {change !== null && (
              <div className="flex items-center gap-1 mt-0.5">
                {change > 0
                  ? <TrendingUp className="w-3 h-3" style={{ color: colors.error }} />
                  : <TrendingDown className="w-3 h-3" style={{ color: '#4ade80' }} />}
                <span className="text-[10px] font-mono" style={{ color: change > 0 ? colors.error : '#4ade80' }}>
                  {change > 0 ? '+' : ''}{change.toFixed(1)}% vs prior period
                </span>
              </div>
            )}
          </div>
        ))}
      </Card>

      {/* Telemetry indicator + time range toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-[#22c55e] animate-pulse" />
          <span className="text-xs" style={{ color: colors.textMuted }}>Telemetry · last {timeRangeLabel}</span>
          {lastUpdatedLabel && (
            <span className="text-xs" style={{ color: colors.textMuted }}>· updated {lastUpdatedLabel}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <SortDropdown<'all' | 'production' | 'staging' | 'development'>
            value={environment as 'all' | 'production' | 'staging' | 'development'}
            options={{ all: 'All envs', production: 'production', staging: 'staging', development: 'development' }}
            onChange={setEnvironment}
            width="w-[120px]"
          />
          <div className="flex items-center gap-1">
            {(['24h', '7d', '30d'] as const).map((r) => (
              <button
                key={r}
                onClick={() => setTimeRange(r)}
                className="px-3 py-1 text-xs font-mono rounded transition-all cursor-pointer"
                style={{
                  background: timeRange === r ? colors.accentSubtle : 'transparent',
                  border: `1px solid ${timeRange === r ? colors.accentBorder : colors.borderDefault}`,
                  color: timeRange === r ? accent : colors.textMuted,
                }}
              >
                {r}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Cost over time chart */}
      <Card className="p-5 sm:p-6">
        <h3 className="text-sm font-semibold mb-5" style={{ color: colors.textPrimary }}>Cost Over Time</h3>
        <ResponsiveContainer width="100%" height={200}>
          <AreaChart data={costSeries} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="costGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={accent} stopOpacity={0.25} />
                <stop offset="95%" stopColor={accent} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.borderSubtle} vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: colors.textMuted, fontSize: 10, fontFamily: 'Geist Mono, monospace' }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fill: colors.textMuted, fontSize: 10, fontFamily: 'Geist Mono, monospace' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v: number) => `$${v.toFixed(2)}`}
              width={52}
            />
            <Tooltip
              contentStyle={{ background: colors.bgBase, border: `1px solid ${colors.borderDefault}`, borderRadius: 6 }}
              labelStyle={{ color: colors.textMuted, fontSize: 11 }}
              itemStyle={{ color: accent, fontSize: 11, fontFamily: 'monospace' }}
              formatter={(v: number) => [`$${v.toFixed(4)}`, 'Cost']}
            />
            <Area
              type="monotone"
              dataKey="cost"
              stroke={accent}
              strokeWidth={2}
              fill="url(#costGrad)"
              dot={false}
              activeDot={{ r: 4, fill: accent, strokeWidth: 0 }}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Card>

      {/* Cost by provider + Volume chart */}
      <div className="grid gap-5 lg:grid-cols-2">
        {/* Cost by provider */}
        <Card className="p-5 sm:p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-semibold" style={{ color: colors.textPrimary }}>Cost by Provider</h3>
            <span className="text-[10px] uppercase tracking-wider" style={{ color: colors.textMuted }}>Last {timeRangeLabel}</span>
          </div>
          <div className="space-y-4">
            {costByProvider.map((p) => {
              const color = PROVIDER_COLORS[p.provider.toLowerCase()] ?? colors.textSecondary;
              const pct = p.monthlyCost > 0 ? (p.monthlyCost / maxProvCost) * 100 : 0;
              return (
                <div key={p.provider} className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span style={{ color: colors.textSecondary }}>{p.provider}</span>
                    <span className="font-mono tabular-nums" style={{ color: p.monthlyCost > 0 ? colors.textPrimary : colors.textMuted }}>
                      {p.monthlyCost > 0 ? fmtCost(p.monthlyCost) : 'free'}
                    </span>
                  </div>
                  <div className="h-1.5 w-full rounded-sm" style={{ background: colors.bgHover }}>
                    {p.monthlyCost > 0 && (
                      <div className="h-1.5 rounded-sm transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
                    )}
                  </div>
                  <p className="text-[10px] font-mono" style={{ color: colors.textMuted }}>
                    {fmtNum(p.callsPerDay)}/day · {p.endpointCount} endpoint{p.endpointCount !== 1 ? 's' : ''}
                  </p>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between mt-5 pt-4" style={{ borderTop: `1px solid ${colors.borderDefault}` }}>
            <span className="text-xs" style={{ color: colors.textSecondary }}>Total</span>
            <span className="text-base font-mono font-bold tabular-nums" style={{ color: accent }}>
              {fmtCost(totalCost)}
            </span>
          </div>
        </Card>

        {/* API Call Volume — live rolling chart */}
        <Card className="flex flex-col p-5 sm:p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-semibold" style={{ color: colors.textPrimary }}>API Call Volume</h3>
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#22c55e' }} />
              <span className="text-[10px] uppercase tracking-wider font-mono" style={{ color: '#22c55e' }}>Live</span>
            </div>
          </div>
          <div className="flex-1" style={{ minHeight: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={livePoints} margin={{ top: 8, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="liveGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={accent} stopOpacity={0.55} />
                    <stop offset="40%"  stopColor={accent} stopOpacity={0.20} />
                    <stop offset="100%" stopColor={accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="t"
                  tick={{ fill: colors.textMuted, fontSize: 10, fontFamily: 'Geist Mono, monospace' }}
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fill: colors.textMuted, fontSize: 10, fontFamily: 'Geist Mono, monospace' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)}
                  width={36}
                />
                <Tooltip
                  contentStyle={{ background: colors.bgBase, border: `1px solid ${colors.borderDefault}`, borderRadius: 6 }}
                  labelStyle={{ color: colors.textMuted, fontSize: 11 }}
                  itemStyle={{ color: accent, fontSize: 11, fontFamily: 'monospace' }}
                  formatter={(v: number) => [fmtNum(v), 'Calls']}
                />
                <Area
                  type="basis"
                  dataKey="calls"
                  stroke={accent}
                  strokeWidth={1.5}
                  strokeOpacity={0.6}
                  fill="url(#liveGrad)"
                  dot={false}
                  activeDot={{ r: 3, fill: accent, strokeWidth: 0 }}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="flex items-center justify-between mt-4 pt-4" style={{ borderTop: `1px solid ${colors.borderDefault}` }}>
            <span className="text-xs" style={{ color: colors.textMuted }}>Daily avg</span>
            <span className="text-sm font-mono tabular-nums" style={{ color: colors.textSecondary }}>
              {fmtNum(Math.round(callsPerDay))} calls
            </span>
          </div>
        </Card>
      </div>

      {/* Endpoints table */}
      {topEndpoints.length > 0 && (
        <div
          className="overflow-hidden"
        >
          <div className="flex items-center justify-between px-5 py-3" style={{ borderBottom: `1px solid ${colors.borderDefault}` }}>
            <h3 className="text-sm font-semibold" style={{ color: colors.textPrimary }}>Endpoints</h3>
            <SortDropdown value={endpointSort} options={SORT_LABELS} onChange={setEndpointSort} />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.borderDefault}` }}>
                  {['Provider', 'Endpoint', 'Method', 'Calls / day', `Cost (${timeRange})`].map((h) => (
                    <th key={h} className="px-5 py-2.5 text-left font-medium" style={{ color: colors.textMuted }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {topEndpoints.map((ep, i) => {
                  const ms = METHOD_STYLES[ep.method] ?? DEFAULT_METHOD_STYLE;
                  const scaledCost = timeRange === '24h'
                    ? ep.monthlyCost / 30
                    : timeRange === '7d'
                    ? ep.monthlyCost / 4.33
                    : ep.monthlyCost;
                  return (
                    <tr key={ep.id} style={{ borderBottom: i < topEndpoints.length - 1 ? `1px solid ${colors.borderSubtle}` : undefined }}>
                      <td className="px-5 py-2.5 font-medium" style={{ color: colors.textSecondary }}>{ep.provider}</td>
                      <td className="px-5 py-2.5 font-mono max-w-[220px] truncate" style={{ color: colors.textPrimary }} title={ep.url}>
                        {ep.url.replace(/^https?:\/\/[^/]+/, '')}
                      </td>
                      <td className="px-5 py-2.5">
                        <Badge color={ms} className="text-[10px]">{ep.method}</Badge>
                      </td>
                      <td className="px-5 py-2.5 font-mono tabular-nums" style={{ color: colors.textSecondary }}>
                        {fmtNum(ep.callsPerDay)}
                      </td>
                      <td className="px-5 py-2.5 font-mono font-semibold tabular-nums" style={{ color: ep.monthlyCost > 0 ? accent : colors.textMuted }}>
                        {ep.monthlyCost > 0 ? fmtCost(scaledCost) : 'free'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Requests */}
      <Card
        className="overflow-hidden"
      >
        <div className="px-5 py-3" style={{ borderBottom: `1px solid ${colors.borderDefault}` }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: colors.textPrimary }}>Recent Requests</h3>
          <div className="flex items-center gap-2">
            <div
              className="flex-1 flex items-center gap-2 px-3 py-1.5 rounded"
              style={{ background: colors.bgSubtle, border: `1px solid ${colors.borderDefault}` }}
            >
              <Search className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.textMuted }} />
              <input
                value={requestSearch}
                onChange={(e) => setRequestSearch(e.target.value)}
                placeholder="Search endpoint or model…"
                className="flex-1 bg-transparent text-xs outline-none placeholder:text-[#a8a29e]"
                style={{ color: colors.textPrimary }}
              />
            </div>
            <SortDropdown<string>
              value={requestProviderFilter}
              options={Object.fromEntries(allProviders.map(p => [p, p])) as Record<string, string>}
              onChange={setRequestProviderFilter}
              width="w-[120px]"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: `1px solid ${colors.borderDefault}` }}>
                {['Timestamp', 'Endpoint', 'Provider', 'Model', 'Tok In', 'Tok Out', 'Cost', 'Latency'].map((h) => (
                  <th key={h} className="px-5 py-2.5 text-left font-medium whitespace-nowrap" style={{ color: colors.textMuted }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRequests.slice(0, visibleRequests).map((r, i) => (
                <tr
                  key={r.id}
                  style={{ borderBottom: i < Math.min(visibleRequests, filteredRequests.length) - 1 ? `1px solid ${colors.borderSubtle}` : undefined }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = colors.bgHover; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = 'transparent'; }}
                >
                  <td className="px-5 py-2.5 font-mono whitespace-nowrap" style={{ color: colors.textMuted }}>{fmtTimestamp(r.timestamp)}</td>
                  <td className="px-5 py-2.5 font-mono max-w-[180px] truncate" style={{ color: colors.textPrimary }} title={r.endpoint}>{r.endpoint}</td>
                  <td className="px-5 py-2.5" style={{ color: colors.textSecondary }}>{r.provider}</td>
                  <td className="px-5 py-2.5 font-mono" style={{ color: colors.textSecondary }}>{r.model ?? '—'}</td>
                  <td className="px-5 py-2.5 font-mono tabular-nums" style={{ color: colors.textMuted }}>{(r.tokensIn ?? 0).toLocaleString()}</td>
                  <td className="px-5 py-2.5 font-mono tabular-nums" style={{ color: colors.textMuted }}>{(r.tokensOut ?? 0).toLocaleString()}</td>
                  <td className="px-5 py-2.5 font-mono font-semibold tabular-nums" style={{ color: accent }}>${r.cost.toFixed(4)}</td>
                  <td className="px-5 py-2.5 font-mono tabular-nums" style={{ color: colors.textMuted }}>{r.latencyMs != null ? `${r.latencyMs}ms` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredRequests.length > visibleRequests && (
          <div className="flex items-center justify-center px-5 py-3" style={{ borderTop: `1px solid ${colors.borderDefault}` }}>
            <button
              onClick={() => setVisibleRequests((n) => n + 10)}
              className="px-4 py-1.5 text-xs rounded-md transition-all cursor-pointer"
              style={{ color: colors.textMuted, border: `1px solid ${colors.borderDefault}`, background: 'transparent' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.textPrimary; (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderHover; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.textMuted; (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderDefault; }}
            >
              Load more
            </button>
          </div>
        )}
      </Card>

    </Motion.div>
  );
}

// ─── Analytics tab ────────────────────────────────────────────────────────────

type AnalyticsPeriod = '7d' | '30d' | '90d';
const MODEL_PALETTE = ['#3b82f6','#8b5cf6','#0891b2','#f97316','#ec4899','#84cc16'];

function ChartSkeleton({ height = 200 }: { height?: number }) {
  return (
    <div
      className="rounded animate-pulse"
      style={{ height, background: colors.bgSubtle, border: `1px solid ${colors.borderSubtle}` }}
    />
  );
}

function AnalyticsTab({ projectId }: { projectId: string }) {
  const [period, setPeriod] = useState<AnalyticsPeriod>('30d');
  const [compare, setCompare] = useState(false);
  const [environment, setEnvironment] = useState('all');

  function getRange(p: AnalyticsPeriod, offset = 0) {
    const days = p === '7d' ? 7 : p === '30d' ? 30 : 90;
    const to = new Date(Date.now() - offset * days * 86_400_000);
    const from = new Date(to.getTime() - days * 86_400_000);
    return { from: from.toISOString(), to: to.toISOString() };
  }

  const envParam = environment !== 'all' ? `&environment=${environment}` : '';
  const { from, to } = getRange(period);
  const { from: prevFrom, to: prevTo } = getRange(period, 1);

  // ── Current period analytics ─────────────────────────────────────────────
  const { data: analytics, isLoading: analyticsLoading, isError: analyticsError } = useQuery<AnalyticsPoint[]>({
    queryKey: analyticsKeys.tab(projectId, period, environment),
    queryFn: () => apiClient
      .get<{ data: AnalyticsApiResponse }>(apiPath`/projects/${projectId}/analytics` + `?from=${from}&to=${to}&interval=day${envParam}`)
      .then(r => toAnalyticsPoints(r.data.data)),
    staleTime: Infinity,
  });

  // ── Previous period analytics (comparison) ───────────────────────────────
  const { data: prevAnalytics } = useQuery<AnalyticsPoint[]>({
    queryKey: analyticsKeys.tabPrev(projectId, period, environment),
    queryFn: () => apiClient
      .get<{ data: AnalyticsApiResponse }>(apiPath`/projects/${projectId}/analytics` + `?from=${prevFrom}&to=${prevTo}&interval=day${envParam}`)
      .then(r => toAnalyticsPoints(r.data.data)),
    enabled: compare,
    staleTime: Infinity,
  });

  // ── Telemetry (model breakdown) ──────────────────────────────────────────
  const { data: telemetry = [] } = useQuery<TelemetryWindow[]>({
    queryKey: analyticsKeys.telemetry(projectId, environment),
    queryFn: () => apiClient
      .get<{ data: TelemetryWindowRaw[] }>(apiPath`/projects/${projectId}/telemetry/recent` + `?limit=50${envParam}`)
      .then(r => flattenTelemetryWindows(r.data)),
    staleTime: Infinity,
  });

  // ── Endpoints ────────────────────────────────────────────────────────────
  const { data: endpoints = [], isLoading: endpointsLoading } = useQuery<EndpointRecord[]>({
    queryKey: analyticsKeys.endpoints(projectId),
    queryFn: () => apiClient
      .get<{ data: EndpointRecord[] }>(apiPath`/projects/${projectId}/endpoints` + `?limit=100`)
      .then(r => r.data),
    staleTime: Infinity,
  });

  // ── Suggestions ──────────────────────────────────────────────────────────
  const { data: suggestions = [], isLoading: suggestionsLoading } = useQuery<Suggestion[]>({
    queryKey: analyticsKeys.suggestions(projectId),
    queryFn: () => apiClient
      .get<{ data: Suggestion[] }>(apiPath`/projects/${projectId}/suggestions` + `?limit=50`)
      .then(r => r.data),
    staleTime: Infinity,
  });

  // ── Data derivations ─────────────────────────────────────────────────────
  function fmtDate(ts: string) {
    const d = new Date(ts);
    return `${d.getMonth() + 1}/${d.getDate()}`;
  }

  function dayKey(ts: string) {
    return ts.slice(0, 10); // YYYY-MM-DD
  }

  const prevByDay = new Map<string, AnalyticsPoint>();
  if (compare && prevAnalytics) {
    for (const p of prevAnalytics) prevByDay.set(dayKey(p.timestamp), p);
  }

  const costSeries = (analytics ?? []).map((p) => {
    const prev = prevByDay.get(dayKey(p.timestamp));
    return {
      label: fmtDate(p.timestamp),
      cost: p.cost,
      prevCost: prev ? prev.cost : null,
    };
  });

  const volSeries = (analytics ?? []).map((p) => {
    const prev = prevByDay.get(dayKey(p.timestamp));
    return {
      label: fmtDate(p.timestamp),
      calls: p.requestCount,
      prevCalls: prev ? prev.requestCount : null,
    };
  });

  // Model totals from telemetry
  const modelTotals = telemetry.reduce((acc, r) => {
    const key = r.model ?? 'unknown';
    acc[key] = (acc[key] ?? 0) + r.cost;
    return acc;
  }, {} as Record<string, number>);
  const modelData = Object.entries(modelTotals)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6)
    .map(([model, totalCost], i) => ({ model, totalCost, color: MODEL_PALETTE[i] ?? colors.textMuted }));
  const modelMax = modelData[0]?.totalCost ?? 1;

  // Top endpoints by cost
  const topEndpoints = [...endpoints]
    .sort((a, b) => b.monthlyCost - a.monthlyCost)
    .slice(0, 10);
  const epMax = topEndpoints[0]?.monthlyCost ?? 1;

  const totalCost = (analytics ?? []).reduce((s, p) => s + p.cost, 0);
  const prevTotalCost = (prevAnalytics ?? []).reduce((s, p) => s + p.cost, 0);

  const periodLabel = period === '7d' ? '7 days' : period === '30d' ? '30 days' : '90 days';

  const tooltipStyle = {
    contentStyle: { background: colors.bgBase, border: `1px solid ${colors.borderDefault}`, borderRadius: 6 },
    labelStyle: { color: colors.textMuted, fontSize: 11 },
    itemStyle: { fontSize: 11, fontFamily: 'monospace' },
  };

  const axisProps = {
    axisLine: false as const,
    tickLine: false as const,
    tick: { fill: colors.textMuted, fontSize: 10, fontFamily: 'Geist Mono, monospace' },
  };

  const hasAnalyticsData = !analyticsLoading && analytics && analytics.length > 0;
  const analyticsSettled = !analyticsLoading && analytics !== undefined;

  if (analyticsError) {
    return (
      <Motion.div {...FADE(0.1)} className="flex flex-col items-center justify-center py-20 text-center">
        <div
          className="w-12 h-12 rounded-lg flex items-center justify-center mb-5"
          style={{ background: colors.errorSubtle, border: `1px solid ${colors.errorBorder}` }}
        >
          <AlertTriangle className="w-5 h-5" style={{ color: colors.error }} />
        </div>
        <h3 className="text-lg font-bold mb-2" style={{ color: colors.textPrimary }}>Failed to load analytics</h3>
        <p className="text-sm" style={{ color: colors.textMuted }}>
          Could not fetch analytics data for this project. Check your connection and try refreshing the page.
        </p>
      </Motion.div>
    );
  }

  if (analyticsSettled && !hasAnalyticsData) {
    return (
      <Motion.div {...FADE(0.1)} className="flex flex-col items-center justify-center py-20 text-center">
        <Card
          className="w-12 h-12 flex items-center justify-center mb-5"
        >
          <Zap className="w-5 h-5" style={{ color: colors.textMuted }} />
        </Card>
        <h3 className="text-lg font-bold mb-2" style={{ color: colors.textPrimary }}>No analytics data yet</h3>
        <p className="text-sm" style={{ color: colors.textMuted }}>
          No requests were recorded in this time window. Try a wider period, or check that the Recost SDK is sending telemetry.
        </p>
      </Motion.div>
    );
  }

  return (
    <Motion.div {...FADE(0.08)} className="space-y-6" data-onboarding-target="analytics-overview">

      {/* ── Control bar ──────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Period */}
        <div className="flex items-center gap-1">
          {(['7d', '30d', '90d'] as const).map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className="px-3 py-1.5 text-xs font-mono rounded-md transition-all cursor-pointer"
              style={{
                background: period === p ? colors.accentSubtle : colors.bgSubtle,
                border: `1px solid ${period === p ? colors.accentBorder : colors.borderDefault}`,
                color: period === p ? accent : colors.textMuted,
              }}
            >
              {p}
            </button>
          ))}
        </div>

        {/* Compare toggle */}
        <button
          onClick={() => setCompare(v => !v)}
          className="flex items-center gap-2 px-3 py-1.5 text-xs rounded-md transition-all cursor-pointer"
          style={{
            background: compare ? colors.accentSubtle : colors.bgSubtle,
            border: `1px solid ${compare ? colors.accentBorder : colors.borderDefault}`,
            color: compare ? accent : colors.textMuted,
          }}
        >
          <span
            className="w-7 h-4 rounded-sm flex items-center transition-all relative"
            style={{ background: compare ? accent : colors.borderDefault }}
          >
            <span
              className="w-3 h-3 rounded-sm absolute transition-all"
              style={{ background: '#fff', left: compare ? '14px' : '2px' }}
            />
          </span>
          Compare to previous period
        </button>

        {/* Environment */}
        <SortDropdown<'all' | 'production' | 'staging' | 'development'>
          value={environment as 'all' | 'production' | 'staging' | 'development'}
          options={{ all: 'All envs', production: 'production', staging: 'staging', development: 'development' }}
          onChange={setEnvironment}
          width="w-[130px]"
        />
      </div>

      {/* ── Cost Trend ───────────────────────────────────────────────────── */}
      <Card className="p-5 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-sm font-semibold" style={{ color: colors.textPrimary }}>Cost Trend</h3>
            {compare && prevTotalCost > 0 && (
              <p className="text-xs mt-0.5" style={{ color: colors.textMuted }}>
                {totalCost < prevTotalCost
                  ? <span style={{ color: '#4ade80' }}>↓ {((prevTotalCost - totalCost) / prevTotalCost * 100).toFixed(1)}% vs prior period</span>
                  : <span style={{ color: colors.error }}>↑ {((totalCost - prevTotalCost) / prevTotalCost * 100).toFixed(1)}% vs prior period</span>
                }
              </p>
            )}
          </div>
          <span className="text-[10px] uppercase tracking-wider" style={{ color: colors.textMuted }}>Last {periodLabel}</span>
        </div>
        {analyticsLoading ? <ChartSkeleton height={220} /> : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={costSeries} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="aGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={accent} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.borderSubtle} vertical={false} />
              <XAxis dataKey="label" {...axisProps} interval="preserveStartEnd" />
              <YAxis {...axisProps} tickFormatter={(v: number) => `$${v.toFixed(2)}`} width={56} />
              <Tooltip
                {...tooltipStyle}
                formatter={(v: number, name: string) => [
                  `$${v.toFixed(4)}`,
                  name === 'cost' ? 'Current' : 'Previous',
                ]}
              />
              {compare && (
                <Area type="monotone" dataKey="prevCost" stroke={colors.textMuted} strokeWidth={1.5}
                  strokeDasharray="4 3" fill="none" dot={false}
                  activeDot={{ r: 3, fill: colors.textMuted, strokeWidth: 0 }} />
              )}
              <Area type="monotone" dataKey="cost" stroke={accent} strokeWidth={2}
                fill="url(#aGrad)" dot={false}
                activeDot={{ r: 4, fill: accent, strokeWidth: 0 }} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* ── Request Volume Density ───────────────────────────────────────── */}
      <Card className="p-5 sm:p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-semibold" style={{ color: colors.textPrimary }}>Request Volume</h3>
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-wider" style={{ color: colors.textMuted }}>Last {periodLabel}</span>
            <span className="text-[10px] uppercase tracking-wider" style={{ color: colors.textMuted }}>· drag to zoom</span>
          </div>
        </div>
        {analyticsLoading ? <ChartSkeleton height={220} /> : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={volSeries} barCategoryGap="2%" margin={{ top: 4, right: 4, left: 0, bottom: 8 }}>
              <defs>
                <linearGradient id="volDensityGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor={accent} stopOpacity={0.9} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0.15} />
                </linearGradient>
                {compare && (
                  <linearGradient id="volDensityPrevGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor={colors.textMuted} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={colors.textMuted} stopOpacity={0.05} />
                  </linearGradient>
                )}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.borderSubtle} vertical={false} />
              <XAxis dataKey="label" {...axisProps} interval="preserveStartEnd" />
              <YAxis {...axisProps}
                tickFormatter={(v: number) => v >= 1000 ? `${(v / 1000).toFixed(1)}k` : String(v)}
                width={40}
              />
              <Tooltip
                {...tooltipStyle}
                cursor={{ fill: colors.borderSubtle, opacity: 0.5 }}
                formatter={(v: number, name: string) => [
                  fmtNum(v),
                  name === 'calls' ? 'Current' : 'Previous',
                ]}
              />
              {compare && (
                <Bar dataKey="prevCalls" fill="url(#volDensityPrevGrad)" radius={[2, 2, 0, 0]} maxBarSize={24} />
              )}
              <Bar dataKey="calls" fill="url(#volDensityGrad)" radius={[2, 2, 0, 0]} maxBarSize={24} />
              <Brush
                dataKey="label"
                height={28}
                stroke={colors.borderDefault}
                fill={colors.bgBase}
                travellerWidth={6}
                tickFormatter={() => ''}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      {/* ── Model breakdown + Top endpoints ──────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Cost by Model */}
        <Card className="p-5 sm:p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-semibold" style={{ color: colors.textPrimary }}>Cost by Model</h3>
            <span className="text-[10px] uppercase tracking-wider" style={{ color: colors.textMuted }}>Approx · recent data</span>
          </div>
          {telemetry.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <p className="text-sm font-medium mb-1" style={{ color: colors.textPrimary }}>No model data yet</p>
              <p className="text-xs" style={{ color: colors.textMuted }}>Model-level breakdown appears once telemetry is active</p>
            </div>
          ) : (
            <div className="space-y-3">
              {modelData.map(({ model, totalCost, color }) => {
                const pct = (totalCost / modelMax) * 100;
                return (
                  <div key={model} className="space-y-1">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-mono truncate max-w-[180px]" style={{ color: colors.textSecondary }} title={model}>{model}</span>
                      <span className="font-mono tabular-nums ml-2" style={{ color: colors.textPrimary }}>{fmtCost(totalCost)}</span>
                    </div>
                    <div className="h-1.5 w-full rounded-sm" style={{ background: colors.bgHover }}>
                      <div className="h-1.5 rounded-sm transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        {/* Top Endpoints by Spend */}
        <Card className="p-5 sm:p-6">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-sm font-semibold" style={{ color: colors.textPrimary }}>Top Endpoints by Spend</h3>
            <span className="text-[10px] uppercase tracking-wider" style={{ color: colors.textMuted }}>Monthly est.</span>
          </div>
          {endpointsLoading ? <ChartSkeleton height={160} /> : topEndpoints.length === 0 ? (
            <div className="flex items-center justify-center py-10">
              <p className="text-xs" style={{ color: colors.textMuted }}>No endpoints yet</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {topEndpoints.map((ep) => {
                const pct = epMax > 0 ? (ep.monthlyCost / epMax) * 100 : 0;
                const path = ep.url.replace(/^https?:\/\/[^/]+/, '');
                return (
                  <div key={ep.id} className="space-y-1">
                    <div className="flex items-center justify-between text-xs gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Badge color={METHOD_STYLES[ep.method] ?? DEFAULT_METHOD_STYLE} className="flex-shrink-0 text-[9px] px-1 py-0.5">{ep.method}</Badge>
                        <span className="font-mono truncate" style={{ color: colors.textSecondary }} title={path}>{path}</span>
                      </div>
                      <span className="font-mono font-semibold tabular-nums flex-shrink-0" style={{ color: ep.monthlyCost > 0 ? accent : colors.textMuted }}>
                        {ep.monthlyCost > 0 ? fmtCost(ep.monthlyCost) : 'free'}
                      </span>
                    </div>
                    <div className="h-1 w-full rounded-sm" style={{ background: colors.bgHover }}>
                      {ep.monthlyCost > 0 && (
                        <div className="h-1 rounded-sm transition-all duration-700" style={{ width: `${pct}%`, background: accent }} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* ── Optimization Suggestions ──────────────────────────────────────── */}
      <Card className="overflow-hidden">
        <div className="px-5 py-4" style={{ borderBottom: `1px solid ${colors.borderDefault}` }}>
          <h3 className="text-sm font-semibold" style={{ color: colors.textPrimary }}>Optimization Suggestions</h3>
        </div>
        {suggestionsLoading ? (
          <div className="p-5 space-y-3">
            {[1, 2, 3].map(i => <ChartSkeleton key={i} height={52} />)}
          </div>
        ) : suggestions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-center">
            <CheckCircle2 className="w-6 h-6 mb-3" style={{ color: '#4ade80' }} />
            <p className="text-sm font-semibold mb-1" style={{ color: colors.textPrimary }}>No suggestions</p>
            <p className="text-xs" style={{ color: colors.textMuted }}>Your API usage looks efficient — no optimization opportunities detected.</p>
          </div>
        ) : (
          <div>
            {suggestions
              .slice()
              .sort((a, b) => {
                const order = { high: 0, medium: 1, low: 2 };
                return order[a.severity] - order[b.severity];
              })
              .map((sg) => {
                const sev = sg.severity === 'high'
                  ? { color: colors.error, bg: colors.errorSubtle, border: colors.errorBorder }
                  : sg.severity === 'medium'
                  ? { color: colors.warning, bg: colors.warningSubtle, border: colors.warningBorder }
                  : { color: colors.textMuted, bg: 'transparent', border: colors.borderDefault };
                return (
                  <div
                    key={sg.id}
                    className="flex items-start gap-3 px-5 py-4"
                    style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}
                  >
                    <span
                      className="flex-shrink-0 mt-0.5 text-[9px] font-mono px-1.5 py-0.5 rounded"
                      style={{ background: sev.bg, border: `1px solid ${sev.border}`, color: sev.color }}
                    >{sg.severity}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium mb-0.5" style={{ color: colors.textPrimary }}>{sg.description}</p>
                      <p className="text-[10px] font-mono" style={{ color: colors.textMuted }}>
                        {SUGGESTION_TYPE_LABELS[sg.type]}
                        {sg.affectedFiles.length > 0 && ` · ${sg.affectedFiles.slice(0, 2).map(f => f.split('/').slice(-1)[0]).join(', ')}`}
                      </p>
                    </div>
                    {sg.estimatedMonthlySavings > 0 && (
                      <span className="flex-shrink-0 text-sm font-mono font-semibold tabular-nums" style={{ color: accent }}>
                        −{fmtCost(sg.estimatedMonthlySavings)}<span className="text-[10px] font-normal">/mo</span>
                      </span>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </Card>

    </Motion.div>
  );
}

// ─── Settings tab ─────────────────────────────────────────────────────────────

// Syntax token colors for the code blocks
const TOKEN = {
  keyword:  '#f97316',
  string:   '#22c55e',
  comment:  '#64748b',
  fn:       '#38bdf8',
  prop:     '#F26B1D',
  plain:    '#0f172a',
  punct:    '#64748b',
};

// Simple syntax highlighter — splits on known patterns and returns <span>s
function HighlightNode({ line }: { line: string }) {
  // keyword pattern
  const parts: React.ReactNode[] = [];
  const raw = line;

  // Line is a comment
  if (raw.trimStart().startsWith('#') || raw.trimStart().startsWith('//')) {
    parts.push(<span key="c" style={{ color: TOKEN.comment }}>{raw}</span>);
    return <>{parts}</>;
  }

  // Simple token scan
  let rem = raw;
  let key = 0;
  const JS_KW = /^(import|from|const|new|await|return|async|function|class|export|default|let|var|if|of|for|in)\b/;
  const PY_KW = /^(from|import|def|class|return|if|else|elif|for|in|with|as|not|and|or|True|False|None)\b/;
  const STR = /^(['"`])[^'"`\n]*\1/;
  const COMMENT_INLINE = /^(\/\/|#).*/;
  const WORD = /^[a-zA-Z_$][\w$]*/;
  const PUNCT = /^[{}()[\].,;:=<>!&|+\-*/\\@]+/;
  const SPACE = /^\s+/;

  while (rem.length > 0) {
    let m: RegExpMatchArray | null;
    if ((m = rem.match(COMMENT_INLINE))) {
      parts.push(<span key={key++} style={{ color: TOKEN.comment }}>{m[0]}</span>);
      rem = rem.slice(m[0].length);
    } else if ((m = rem.match(STR))) {
      parts.push(<span key={key++} style={{ color: TOKEN.string }}>{m[0]}</span>);
      rem = rem.slice(m[0].length);
    } else if ((m = rem.match(JS_KW)) || (m = rem.match(PY_KW))) {
      parts.push(<span key={key++} style={{ color: TOKEN.keyword }}>{m[0]}</span>);
      rem = rem.slice(m[0].length);
    } else if ((m = rem.match(WORD))) {
      parts.push(<span key={key++} style={{ color: TOKEN.plain }}>{m[0]}</span>);
      rem = rem.slice(m[0].length);
    } else if ((m = rem.match(PUNCT))) {
      parts.push(<span key={key++} style={{ color: TOKEN.punct }}>{m[0]}</span>);
      rem = rem.slice(m[0].length);
    } else if ((m = rem.match(SPACE))) {
      parts.push(<span key={key++}>{m[0]}</span>);
      rem = rem.slice(m[0].length);
    } else {
      parts.push(<span key={key++}>{rem[0]}</span>);
      rem = rem.slice(1);
    }
  }
  return <>{parts}</>;
}

function CodeBlock({ code, onCopy }: { code: string; onCopy: () => void }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    onCopy();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div
      className="relative rounded-md overflow-hidden"
      style={{
        background: '#f8fafc',
        border: '1px solid rgba(148,163,184,0.24)',
        boxShadow: '0 10px 24px rgba(15,23,42,0.06)',
      }}
    >
      <button
        onClick={handleCopy}
        aria-label={copied ? 'Copied' : 'Copy code snippet'}
        className="absolute top-2.5 right-2.5 flex items-center gap-1.5 px-2.5 py-1 rounded text-[10px] font-mono transition-all cursor-pointer z-10 focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)]"
        style={{
          background: copied ? colors.accentSubtle : '#ffffff',
          border: `1px solid ${copied ? colors.accentBorder : 'rgba(148,163,184,0.24)'}`,
          color: copied ? accent : colors.textSecondary,
        }}
      >
        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        {copied ? 'Copied!' : 'Copy'}
      </button>
      <pre
        className="px-5 py-4 text-[12px] font-mono leading-relaxed overflow-x-auto"
        style={{ margin: 0, color: TOKEN.plain, background: 'transparent' }}
      >
        {code.split('\n').map((line, i) => (
          <div key={i}><HighlightNode line={line} /></div>
        ))}
      </pre>
    </div>
  );
}

// Inline-editable field
function InlineEdit({
  value,
  placeholder,
  onSave,
  multiline = false,
}: {
  value: string;
  placeholder?: string;
  onSave: (v: string) => Promise<void>;
  multiline?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  async function save() {
    const trimmed = draft.trim();
    if (trimmed === value) { setEditing(false); return; }
    if (!multiline && (trimmed.length === 0 || trimmed.length > 100)) {
      toast.error('Name must be 1–100 characters');
      setDraft(value);
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(trimmed || value);
      setEditing(false);
    } catch {
      setDraft(value);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !multiline) { e.preventDefault(); save(); }
    if (e.key === 'Escape') { setDraft(value); setEditing(false); }
  }

  if (editing) {
    const sharedStyle: React.CSSProperties = {
      background: colors.bgSubtle,
      border: `1px solid ${colors.accentBorder}`,
      borderRadius: '6px',
      color: colors.textPrimary,
      fontSize: '14px',
      padding: '4px 8px',
      outline: 'none',
      width: '100%',
      opacity: saving ? 0.6 : 1,
    };

    return multiline ? (
      <textarea
        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={onKeyDown}
        disabled={saving}
        rows={3}
        style={{ ...sharedStyle, resize: 'vertical', fontFamily: 'inherit' }}
      />
    ) : (
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={onKeyDown}
        disabled={saving}
        maxLength={100}
        style={sharedStyle}
      />
    );
  }

  const isEmpty = !value;
  return (
    <UiTooltip>
      <TooltipTrigger asChild>
        <button
          onClick={() => setEditing(true)}
          className="group flex items-start gap-2 text-left w-full cursor-pointer"
          style={{ background: 'none', border: 'none', padding: 0 }}
        >
          <span
            className="text-sm leading-snug flex-1"
            style={{ color: isEmpty ? colors.textMuted : colors.textPrimary }}
          >
            {isEmpty ? placeholder : value}
          </span>
          <Pencil
            className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity"
            style={{ color: colors.textMuted }}
          />
        </button>
      </TooltipTrigger>
      <TooltipContent>Click to edit</TooltipContent>
    </UiTooltip>
  );
}

// Skeleton placeholder
function Skel({ w = 'w-32', h = 'h-4' }: { w?: string; h?: string }) {
  return <div className={`${w} ${h} rounded animate-pulse`} style={{ background: colors.bgHover }} />;
}

function SettingsTab({ projectId, project }: { projectId: string; project: Project | null }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();

  // snippet tabs
  const [snippetTab, setSnippetTab] = useState<'node' | 'python'>('node');

  // delete modal state
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  // ── Derived ───────────────────────────────────────────────────────────────
  const nodeSnippet = `import { init } from '@recost-dev/node';

init({
  apiKey:    process.env.RECOST_API_KEY,
  projectId: '${projectId}',
});`;

  const pythonSnippet = `from recost import init, RecostConfig

init(RecostConfig(
    api_key="<YOUR_API_KEY>",
    project_id="${projectId}",
))`;

  // ── Mutations ─────────────────────────────────────────────────────────────
  const renameMutation = useMutation({
    mutationFn: (name: string) =>
      apiClient.patch<{ data: Project }>(apiPath`/projects/${projectId}`, { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard-project', projectId] });
      qc.invalidateQueries({ queryKey: ['dashboard-projects'] });
      toast.success('Project name updated');
    },
    onError: (e: Error) => toast.error(e.message || 'Failed to update name'),
  });

  const descMutation = useMutation({
    mutationFn: (description: string) =>
      apiClient.patch<{ data: Project }>(apiPath`/projects/${projectId}`, { description }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard-project', projectId] });
      toast.success('Description updated');
    },
    onError: (e: Error) => toast.error(e.message || 'Failed to update description'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.del(apiPath`/projects/${projectId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard-projects'] });
      setShowDelete(false);
      toast.success(`Project "${project?.name}" has been deleted`);
      navigate('/dashboard/projects');
    },
    onError: (e: Error) => toast.error(e.message || 'Failed to delete project'),
  });

  // ── Copy helpers ──────────────────────────────────────────────────────────
  function copySnippet() {
    const text = snippetTab === 'node' ? nodeSnippet : pythonSnippet;
    navigator.clipboard.writeText(text).catch(() => {});
  }

  function copyProjectId() {
    navigator.clipboard.writeText(projectId).then(() => {
      toast.success('Project ID copied!', { duration: 1800 });
    }).catch(() => {});
  }

  const loading = !project;

  return (
    <Motion.div {...FADE(0.08)} className="space-y-5">

      {/* ── 1. Project ID Banner ─────────────────────────────────────────── */}
      <div
        className="rounded-lg px-6 py-5"
        style={{ background: colors.bgBase, border: `1px solid ${colors.accentBorder}` }}
      >
        <p className="text-[10px] uppercase tracking-[0.14em] mb-3 font-semibold" style={{ color: accent }}>
          Project ID
        </p>
        {loading ? (
          <Skel w="w-48" h="h-8" />
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            <code
              className="text-xl sm:text-2xl font-mono font-semibold tracking-tight"
              style={{ color: colors.textPrimary }}
            >
              {projectId}
            </code>
            <button
              id="copy-project-id-btn"
              onClick={copyProjectId}
              aria-label="Copy project ID"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-mono transition-all cursor-pointer focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)]"
              style={{
                background: colors.accentSubtle,
                border: `1px solid ${colors.accentBorder}`,
                color: accent,
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentHover; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = colors.accentSubtle; }}
            >
              <Copy className="w-3.5 h-3.5" />
              Copy
            </button>
          </div>
        )}
        <p className="text-xs mt-3" style={{ color: colors.textMuted }}>
          API keys are managed in your{' '}
          <Link
            to="/dashboard/account"
            className="transition-colors"
            style={{ color: colors.textSecondary, textDecoration: 'underline', textUnderlineOffset: '3px' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textPrimary; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textSecondary; }}
          >
            Account settings
          </Link>
        </p>
      </div>

      {/* ── 2. Project Info ──────────────────────────────────────────────── */}
      <Card
        className="overflow-hidden"
      >
        <div className="px-5 py-3.5" style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}>
          <p className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: colors.textMuted }}>
            Project Info
          </p>
        </div>

        {/* Name row */}
        <div
          className="flex items-start gap-6 px-5 py-4"
          style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}
        >
          <span className="text-xs mt-1 w-28 flex-shrink-0 font-medium" style={{ color: colors.textMuted }}>
            Project Name
          </span>
          <div className="flex-1 min-w-0">
            {loading ? <Skel w="w-40" /> : (
              <InlineEdit
                value={project!.name}
                placeholder="Enter project name…"
                onSave={(v) => renameMutation.mutateAsync(v).then(() => {})}
              />
            )}
          </div>
        </div>

        {/* Description row */}
        <div
          className="flex items-start gap-6 px-5 py-4"
          style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}
        >
          <span className="text-xs mt-1 w-28 flex-shrink-0 font-medium" style={{ color: colors.textMuted }}>
            Description
          </span>
          <div className="flex-1 min-w-0">
            {loading ? <Skel w="w-64" /> : (
              <InlineEdit
                value={project!.description ?? ''}
                placeholder="Add a description…"
                onSave={(v) => descMutation.mutateAsync(v).then(() => {})}
                multiline
              />
            )}
          </div>
        </div>

        {/* Created row */}
        <div
          className="flex items-start gap-6 px-5 py-4"
          style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}
        >
          <span className="text-xs mt-0.5 w-28 flex-shrink-0 font-medium" style={{ color: colors.textMuted }}>
            Created
          </span>
          {loading
            ? <Skel w="w-32" />
            : <span className="text-sm" style={{ color: colors.textSecondary }}>{fmt(project!.createdAt)}</span>}
        </div>

        {/* Plan tier row */}
        <div className="flex items-center gap-6 px-5 py-4">
          <span className="text-xs w-28 flex-shrink-0 font-medium" style={{ color: colors.textMuted }}>
            Plan Tier
          </span>
          <div className="flex items-center gap-3">
            <span
              className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold rounded"
              style={{
                background: colors.accentSubtle,
                border: `1px solid ${colors.accentBorder}`,
                color: accent,
              }}
            >
              Free
            </span>
            <span className="text-xs" style={{ color: colors.textMuted }}>·</span>
            <button
              className="text-xs transition-colors cursor-pointer"
              style={{ color: colors.textMuted, background: 'none', border: 'none', padding: 0, textDecoration: 'underline', textUnderlineOffset: '3px' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.textSecondary; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.textMuted; }}
            >
              Upgrade
            </button>
          </div>
        </div>
      </Card>

      {/* ── 3. Quick Start Integration ───────────────────────────────────── */}
      <div
        className="rounded-lg overflow-hidden"
        data-onboarding-target="settings-quick-start"
        style={{ background: colors.bgBase, border: `1px solid ${colors.borderDefault}` }}
      >
        {/* Header + tab pills */}
        <div
          className="flex items-center justify-between px-5 py-3.5"
          style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}
        >
          <div className="flex items-center gap-2">
            <Terminal className="w-3.5 h-3.5" style={{ color: colors.textMuted }} />
            <p className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: colors.textMuted }}>
              Quick Start
            </p>
          </div>
          <div className="flex items-center gap-1">
            {(['node', 'python'] as const).map((lang) => {
              const active = snippetTab === lang;
              return (
                <button
                  key={lang}
                  onClick={() => setSnippetTab(lang)}
                  className="relative px-3 py-1.5 text-xs font-mono transition-all cursor-pointer rounded"
                  style={{
                    color: active ? colors.textPrimary : colors.textMuted,
                    background: active ? colors.bgHover : 'transparent',
                    border: active ? `1px solid ${colors.borderDefault}` : '1px solid transparent',
                  }}
                >
                  {lang === 'node' ? 'Node.js' : 'Python'}
                  {active && (
                    <span
                      className="absolute inset-x-2 -bottom-px h-[2px]"
                      style={{ background: accent }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Code block */}
        <div className="px-5 py-4">
          <CodeBlock
            code={snippetTab === 'node' ? nodeSnippet : pythonSnippet}
            onCopy={copySnippet}
          />
          <p className="text-xs mt-3" style={{ color: colors.textMuted }}>
            Grab your API key from{' '}
            <Link
              to="/dashboard/account"
              className="transition-colors"
              style={{ color: colors.textSecondary, textDecoration: 'underline', textUnderlineOffset: '3px' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textPrimary; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = colors.textSecondary; }}
            >
              Account → API Keys
            </Link>
          </p>
        </div>
      </div>

      {/* ── 4. Team Members (Coming Soon) ────────────────────────────────── */}
      <Card
        className="overflow-hidden"
      >
        {/* Header */}
        <div
          className="flex items-center gap-3 px-5 py-3.5"
          style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}
        >
          <Users className="w-3.5 h-3.5" style={{ color: colors.textMuted }} />
          <p className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: colors.textMuted }}>
            Team Members
          </p>
          <span
            className="inline-flex items-center px-2 py-0.5 text-[10px] font-mono rounded"
            style={{
              background: colors.bgHover,
              border: `1px solid ${colors.borderDefault}`,
              color: colors.textMuted,
            }}
          >
            Coming Soon
          </span>
        </div>

        {/* Preview (muted) with overlay */}
        <div className="relative">
          <div className="opacity-30 pointer-events-none select-none">
            {/* Table header */}
            <div
              className="grid px-5 py-2.5"
              style={{
                gridTemplateColumns: '1fr 120px 140px 80px',
                borderBottom: `1px solid ${colors.borderSubtle}`,
              }}
            >
              {['Email', 'Role', 'Joined', 'Actions'].map((h) => (
                <span key={h} className="text-[10px] uppercase tracking-[0.1em]" style={{ color: colors.textMuted }}>
                  {h}
                </span>
              ))}
            </div>
            {/* Owner row */}
            <div
              className="grid items-center px-5 py-3.5"
              style={{ gridTemplateColumns: '1fr 120px 140px 80px', borderBottom: `1px solid ${colors.borderSubtle}` }}
            >
              <span className="text-sm font-mono truncate" style={{ color: colors.textPrimary }}>
                {user?.email ?? 'you@example.com'}
              </span>
              <span
                className="inline-flex items-center w-fit px-2 py-0.5 text-[10px] font-mono rounded"
                style={{ background: colors.accentSubtle, border: `1px solid ${colors.accentBorder}`, color: accent }}
              >
                Owner
              </span>
              <span className="text-xs" style={{ color: colors.textMuted }}>
                {fmt(user?.createdAt)}
              </span>
              <span />
            </div>
            {/* Invite button */}
            <div className="px-5 py-4">
              <button
                disabled
                className="px-4 py-2 text-xs font-medium rounded-md opacity-50 cursor-not-allowed"
                style={{
                  background: colors.bgSubtle,
                  border: `1px solid ${colors.borderDefault}`,
                  color: colors.textMuted,
                }}
              >
                Invite Member
              </button>
            </div>
          </div>

          {/* Overlay message */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="px-4 py-3 rounded-lg text-center max-w-xs"
              style={{
                background: 'rgba(255,255,255,0.92)',
                border: `1px solid ${colors.borderDefault}`,
                backdropFilter: 'blur(6px)',
                boxShadow: '0 10px 24px rgba(15,23,42,0.06)',
              }}
            >
              <p className="text-[10px] uppercase tracking-[0.12em] font-semibold mb-1" style={{ color: accent }}>
                Team management is coming soon
              </p>
              <p className="text-xs leading-relaxed" style={{ color: colors.textMuted }}>
                Currently all projects are single-user.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* ── 5. Danger Zone ───────────────────────────────────────────────── */}
      <div
        className="rounded-lg overflow-hidden mt-8"
        style={{
          background: 'rgba(248,113,113,0.03)',
          border: '1px solid rgba(248,113,113,0.16)',
          marginTop: '2.5rem',
        }}
      >
        {/* Header */}
        <div
          className="px-5 py-3.5 flex items-center gap-2"
          style={{ borderBottom: '1px solid rgba(248,113,113,0.10)' }}
        >
          <p className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ color: '#fb7185' }}>
            Danger Zone
          </p>
        </div>

        <div className="px-5 py-5 space-y-4">
          {/* Delete Project */}
          <div className="flex flex-wrap items-start gap-4">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold mb-0.5" style={{ color: colors.textPrimary }}>Delete Project</p>
              <p className="text-xs" style={{ color: colors.textMuted }}>
                Permanently delete this project and all associated data. This action cannot be undone.
              </p>
            </div>
            <button
              id="delete-project-btn"
              onClick={() => { setShowDelete(true); setDeleteConfirm(''); }}
              className="flex items-center justify-center gap-2 w-[152px] py-2 text-sm font-medium rounded-md transition-all cursor-pointer flex-shrink-0"
              style={{
                color: '#fb7185',
                border: '1px solid rgba(248,113,113,0.24)',
                background: 'rgba(248,113,113,0.04)',
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(248,113,113,0.10)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete Project
            </button>
          </div>

          {/* Reset Telemetry */}
          <div
            className="flex flex-wrap items-start gap-4 pt-4"
            style={{ borderTop: '1px solid rgba(248,113,113,0.10)' }}
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold mb-0.5" style={{ color: colors.textPrimary }}>Reset Telemetry Data</p>
              <p className="text-xs" style={{ color: colors.textMuted }}>
                Clear all telemetry records for this project while keeping the project itself.
              </p>
            </div>
            <div className="relative group flex-shrink-0">
              <button
                disabled
                className="flex items-center justify-center gap-2 w-[152px] py-2 text-sm font-medium rounded-md cursor-not-allowed opacity-35 flex-shrink-0"
                style={{
                  color: '#fb7185',
                  border: '1px solid rgba(248,113,113,0.24)',
                  background: 'rgba(248,113,113,0.03)',
                }}
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Reset Telemetry
              </button>
              {/* Tooltip */}
              <div
                className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-2.5 py-1.5 rounded text-xs whitespace-nowrap pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity z-10"
                style={{
                  background: colors.bgBase,
                  border: `1px solid ${colors.borderDefault}`,
                  color: colors.textMuted,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                }}
              >
                Telemetry reset is not yet available
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Delete Confirmation Modal ─────────────────────────────────────── */}
      <Dialog
        open={showDelete}
        onOpenChange={(open) => {
          if (!open) {
            setShowDelete(false);
            setDeleteConfirm('');
          }
        }}
      >
        <DialogContent
          aria-labelledby="delete-dialog-title"
          showCloseButton={false}
          className="w-full max-w-sm rounded-lg shadow-2xl overflow-hidden p-0"
          style={{ background: colors.bgBase, border: `1px solid ${colors.borderDefault}` }}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            document.getElementById('delete-confirm-input')?.focus();
          }}
        >
              {/* Modal header */}
              <div
                className="flex items-center justify-between px-5 py-4"
                style={{ borderBottom: `1px solid ${colors.borderSubtle}` }}
              >
                <div className="flex items-center gap-2.5">
                  <Trash2 className="w-4 h-4" style={{ color: '#f87171' }} />
                  <h2 id="delete-dialog-title" className="text-[15px] font-bold" style={{ color: colors.textPrimary }}>
                    Delete Project
                  </h2>
                </div>
                <button
                  onClick={() => { setShowDelete(false); setDeleteConfirm(''); }}
                  aria-label="Close"
                  className="p-1.5 rounded transition-colors cursor-pointer focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)]"
                  style={{ color: colors.textMuted, background: 'none', border: 'none' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.textPrimary; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = colors.textMuted; }}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Modal body */}
              <div className="px-5 py-5">
                <div
                  className="flex items-start gap-2.5 px-3.5 py-3 rounded-md mb-5"
                  style={{ background: colors.errorSubtle, border: `1px solid ${colors.errorBorder}` }}
                >
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: colors.error }} />
                  <p className="text-sm leading-relaxed" style={{ color: colors.error }}>
                    This will permanently delete <strong>{project?.name}</strong> and all associated data including
                    telemetry, scans, endpoints, suggestions, and analysis results. This action cannot be undone.
                  </p>
                </div>

                <label className="block mb-1.5 text-xs font-medium" style={{ color: colors.textMuted }}>
                  Type <code className="font-mono px-1" style={{ color: colors.textPrimary }}>{project?.name}</code> to confirm
                </label>
                <input
                  id="delete-confirm-input"
                  autoFocus
                  value={deleteConfirm}
                  onChange={(e) => setDeleteConfirm(e.target.value)}
                  placeholder={project?.name}
                  className="w-full px-4 py-2.5 rounded-md text-sm placeholder:text-[#a8a29e] focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)] transition-all mb-5"
                  style={{
                    background: colors.bgSubtle,
                    border: `1px solid ${deleteConfirm === project?.name ? colors.errorBorder : colors.borderDefault}`,
                    color: colors.textPrimary,
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = colors.borderHover; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = deleteConfirm === project?.name ? colors.errorBorder : colors.borderDefault; }}
                  onKeyDown={(e) => { if (e.key === 'Enter' && deleteConfirm === project?.name && !deleteMutation.isPending) deleteMutation.mutate(); }}
                />

                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowDelete(false); setDeleteConfirm(''); }}
                    className="flex-1 px-4 py-2.5 rounded-md text-sm transition-all cursor-pointer"
                    style={{ background: colors.bgSubtle, border: `1px solid ${colors.borderDefault}`, color: colors.textMuted }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderHover; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = colors.borderDefault; }}
                  >
                    Cancel
                  </button>
                  <button
                    id="confirm-delete-btn"
                    onClick={() => deleteMutation.mutate()}
                    disabled={deleteConfirm !== project?.name || deleteMutation.isPending}
                    className="flex-1 px-4 py-2.5 rounded-md text-sm font-medium transition-all cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                    style={{
                      background: deleteConfirm === project?.name ? colors.errorSubtle : 'transparent',
                      border: `1px solid ${deleteConfirm === project?.name ? colors.errorBorder : colors.borderDefault}`,
                      color: deleteConfirm === project?.name ? colors.error : colors.textMuted,
                    }}
                  >
                    {deleteMutation.isPending ? 'Deleting…' : 'Delete Project'}
                  </button>
                </div>
              </div>
        </DialogContent>
      </Dialog>

    </Motion.div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ProjectDetail() {
  const params = useParams();
  const id = params.id as string;
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [scansPage, setScansPage] = useState(1);
  const [searchParams] = useSearchParams();
  const TAB_VALUES = ['static', 'live', 'analytics', 'settings'] as const;
  type Tab = typeof TAB_VALUES[number];
  const rawTab = searchParams.get('view') as Tab;
  const activeTab: Tab = TAB_VALUES.includes(rawTab) ? rawTab : 'static';
  const onboarding = useOnboarding({ userId: user?.id, projectId: id });
  const onboardingStepIndex = onboarding.activeTour?.step ?? 0;
  const onboardingStep = ONBOARDING_TOUR_STEPS[Math.min(onboardingStepIndex, ONBOARDING_TOUR_STEPS.length - 1)];

  const { data: project, isLoading: projectLoading, isError: projectError, refetch: refetchProject } =
    useQuery<Project>({
      queryKey: ['dashboard-project', id],
      queryFn: () => apiClient.get<{ data: Project }>(apiPath`/projects/${id}`).then((r) => r.data),
      enabled: !!id,
    });

  // Latest scan (for summary stats + scan ID)
  const { data: latestScan, isLoading: latestScanLoading } = useQuery<Scan | null>({
    queryKey: ['dashboard-project-latest-scan', id],
    queryFn: () =>
      apiClient
        .get<{ data: Scan }>(apiPath`/projects/${id}/scans/latest`)
        .then((r) => r.data)
        .catch(() => null),
    enabled: !!id,
  });

  // Endpoints for the latest scan — paginated via "load more" (Wave 5 #45).
  // useInfiniteQuery accumulates pages; flattenPages collapses them back into a
  // flat EndpointRecord[] so the provider grouping + client-side sort below are
  // unchanged. The API already returns the { data, pagination } envelope.
  const {
    data: endpointsPages,
    isLoading: endpointsLoading,
    isError: endpointsError,
    refetch: refetchEndpoints,
    fetchNextPage: fetchMoreEndpoints,
    hasNextPage: hasMoreEndpoints,
    isFetchingNextPage: isFetchingMoreEndpoints,
  } = useInfiniteQuery({
    queryKey: ['dashboard-project-endpoints', id, latestScan?.id],
    queryFn: ({ pageParam }) =>
      apiClient.get<{ data: EndpointRecord[]; pagination: { page: number; hasNext: boolean } }>(
        apiPath`/projects/${id}/endpoints` + `?scanId=${latestScan!.id}&page=${pageParam}&limit=100`
      ),
    initialPageParam: 1,
    getNextPageParam: nextPageParam,
    enabled: !!id && !!latestScan?.id,
  });
  const endpoints: EndpointRecord[] = flattenPages(endpointsPages?.pages);

  // Suggestions for the latest scan
  const { data: suggestions = [], isLoading: suggestionsLoading } =
    useQuery<Suggestion[]>({
      queryKey: ['dashboard-project-suggestions', id, latestScan?.id],
      queryFn: () =>
        apiClient
          .get<{ data: Suggestion[] }>(
            apiPath`/projects/${id}/suggestions` + `?scanId=${latestScan!.id}&limit=50`
          )
          .then((r) => r.data),
      enabled: !!id && !!latestScan?.id,
    });

  // Scan history
  const { data: scansResponse, isLoading: scansLoading, isError: scansError, refetch: refetchScans } =
    useQuery<{ data: Scan[]; pagination: { page: number; totalPages: number; hasNext: boolean; hasPrev: boolean } }>({
      queryKey: ['dashboard-project-scans', id, scansPage],
      queryFn: () =>
        apiClient
          .get<{ data: Scan[]; pagination: { page: number; totalPages: number; hasNext: boolean; hasPrev: boolean } }>(apiPath`/projects/${id}/scans` + `?page=${scansPage}&limit=50`)
          .then((r) => r),
      enabled: !!id && historyOpen,
    });
  const scans = scansResponse?.data ?? [];
  const scansPagination = scansResponse?.pagination;

  const deleteScanMutation = useMutation({
    mutationFn: (scanId: string) => apiClient.del(apiPath`/projects/${id}/scans/${scanId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboard-project-scans', id] });
      qc.invalidateQueries({ queryKey: ['dashboard-project', id] });
      qc.invalidateQueries({ queryKey: ['dashboard-project-latest-scan', id] });
      qc.invalidateQueries({ queryKey: ['dashboard-project-endpoints', id] });
      qc.invalidateQueries({ queryKey: ['dashboard-project-suggestions', id] });
      // Live + Analytics tabs (#43): prefix-invalidate so a scan delete refreshes
      // telemetry/analytics caches too. Prefixes match the parameterized keys.
      for (const prefix of scanDeleteInvalidationPrefixes) {
        qc.invalidateQueries({ queryKey: [prefix] });
      }
    },
  });

  const isLoading = projectLoading || latestScanLoading;
  const summary = latestScan?.summary;
  const totalSavings = suggestions.reduce((s, sg) => s + sg.estimatedMonthlySavings, 0);

  // Risk breakdown from endpoint statuses / frequency classes
  const riskBreakdown: { label: string; count: number }[] = [];
  const nPlusOneCount   = endpoints.filter((ep) => ep.status === 'n_plus_one_risk').length;
  const rateLimitCount  = endpoints.filter((ep) => ep.status === 'rate_limit_risk').length;
  const unboundedCount  = endpoints.filter((ep) => ep.frequencyClass === 'unbounded-loop').length;
  if (nPlusOneCount  > 0) riskBreakdown.push({ label: 'N+1',            count: nPlusOneCount });
  if (rateLimitCount > 0) riskBreakdown.push({ label: 'rate limit',     count: rateLimitCount });
  if (unboundedCount > 0) riskBreakdown.push({ label: 'unbounded-loop', count: unboundedCount });

  // Group endpoints by provider for the static provider-breakdown section
  const providerGroupMap = new Map<string, EndpointRecord[]>();
  for (const ep of endpoints) {
    if (!providerGroupMap.has(ep.provider)) providerGroupMap.set(ep.provider, []);
    providerGroupMap.get(ep.provider)!.push(ep);
  }
  const grandTotal = endpoints.reduce((s, ep) => s + ep.monthlyCost, 0);
  const providerGroups = [...providerGroupMap.entries()]
    .map(([provider, eps]) => ({
      provider,
      endpoints: [...eps].sort((a, b) => b.monthlyCost - a.monthlyCost),
      totalCost: eps.reduce((s, ep) => s + ep.monthlyCost, 0),
    }))
    .sort((a, b) => b.totalCost - a.totalCost);

  // Top cost driver callout
  const topEndpoint = endpoints.length > 0
    ? [...endpoints].sort((a, b) => b.monthlyCost - a.monthlyCost)[0]
    : null;
  const topEndpointSuggestion = topEndpoint
    ? suggestions.find((sg) => sg.affectedEndpoints.includes(topEndpoint.id)) ?? null
    : null;

  useEffect(() => {
    if (!onboarding.shouldShowProjectTour) return;
    if (activeTab !== onboardingStep.tab) {
      navigate(`/dashboard/projects/${id}?view=${onboardingStep.tab}`, { replace: true });
    }
  }, [activeTab, id, navigate, onboarding.shouldShowProjectTour, onboardingStep.tab]);

  return (
    <div className="min-h-full">
      <div className="w-full px-6 md:px-10 pt-8 pb-16">
        {/* Project header */}
        {isLoading ? (
          <CenteredSpinner />
        ) : projectError ? (
          <Motion.div {...FADE(0.05)}>
            <ErrorState message="Failed to load project" onRetry={() => refetchProject()} />
          </Motion.div>
        ) : project ? (
          <Motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, ease: 'easeOut', delay: 0.05 }} className="mb-0" data-onboarding-target="project-header">
            <p className="text-xs uppercase tracking-[0.12em] mb-2" style={{ color: accent }}>Project</p>
            <div className="flex items-end justify-between gap-8 pb-5">
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl sm:text-3xl font-bold break-words" style={{ color: colors.textPrimary }}>
                  {project.name}
                </h1>
              </div>
              {/* Tab switcher — top right */}
              <div
                className="flex items-center gap-7 flex-shrink-0"
                data-onboarding-target="project-tabs"
                style={{ alignSelf: 'flex-end' }}
              >
                {(['static', 'live', 'analytics', 'settings'] as const).map((tab) => {
                  const isActive = activeTab === tab;
                  return (
                    <Link
                      key={tab}
                      to={`?view=${tab}`}
                      className="relative pb-3 text-sm font-medium capitalize transition-colors"
                      style={{ color: isActive ? colors.textPrimary : colors.textMuted, textDecoration: 'none' }}
                    >
                      {tab}
                      {isActive && (
                        <span
                          className="absolute inset-x-0 -bottom-px h-[2px]"
                          style={{ background: accent }}
                        />
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
            {/* Full-width bottom border */}
            <div style={{ borderBottom: `1px solid ${colors.borderDefault}` }} className="mb-8" />
          </Motion.div>
        ) : null}

        {/* Live tab */}
        {activeTab === 'live' && (
          <LiveTab projectId={id} />
        )}

        {/* Static tab: scan report */}
        {activeTab === 'static' && <>

        {/* Empty state — no scans yet */}
        {!latestScanLoading && !latestScan && (
          <Motion.div {...FADE(0.1)}>
            <EmptyState
              icon={<FileCode className="w-5 h-5" style={{ color: colors.textMuted }} />}
              title="No scans yet"
              description="Run a scan with the Recost CLI or SDK to see your static analysis — endpoint breakdown, cost projections, and optimization suggestions."
            />
          </Motion.div>
        )}

        {/* ── 1 + 2. Scan summary, top cost driver, cost by provider ──────── */}
        <Motion.div {...FADE(0)} className="flex gap-3 mb-8" data-onboarding-target="static-overview" style={{ alignItems: 'stretch' }}>

          {/* Left column — Scan Summary + Top Cost Driver stacked */}
          <div className="flex flex-col gap-3" style={{ flex: '1 1 0', minWidth: 0 }}>

          {summary && (
            <>
            {/* Scan Summary card */}
            <div
              className="flex flex-col rounded-lg p-5 sm:p-6"
              style={{
                background: colors.bgBase,
                border: `1px solid ${colors.borderDefault}`,
              }}
            >
              {/* Top row: label left, scan date right */}
              <div className="flex items-center justify-between mb-3">
                <p className="text-[10px] uppercase tracking-[0.12em]" style={{ color: colors.textMuted }}>
                  Monthly Cost
                </p>
                {(latestScan?.created_at ?? latestScan?.createdAt) && (
                  <p className="text-xs font-mono" style={{ color: colors.textSecondary }}>
                    Latest scan · {fmt(latestScan.created_at ?? latestScan.createdAt)}
                  </p>
                )}
              </div>

              {/* Cost + saveable row */}
              <div className="flex items-baseline justify-between">
                <p
                  className="text-[2.75rem] font-bold font-mono tabular-nums leading-none"
                  style={{ color: summary.totalMonthlyCost > 0 ? accent : colors.textPrimary }}
                >
                  {fmtCost(summary.totalMonthlyCost)}
                </p>
                {totalSavings > 0 && (
                  <p className="text-sm font-mono" style={{ color: colors.textSecondary }}>
                    ~{fmtCost(totalSavings)}/mo saveable
                  </p>
                )}
              </div>

              {/* Divider */}
              <div className="mt-5 mb-5" style={{ height: 1, background: colors.borderDefault }} />

              {/* Three stat columns with vertical dividers */}
              <div className="flex items-stretch">
                {/* Endpoints */}
                <div className="flex-1 flex flex-col justify-start">
                  <p className="text-[2rem] font-bold font-mono tabular-nums leading-none" style={{ color: colors.textPrimary }}>
                    {summary.totalEndpoints}
                  </p>
                  <p className="text-xs font-mono mt-1.5" style={{ color: colors.textMuted }}>endpoints</p>
                </div>

                <div className="mx-5 self-stretch" style={{ width: 1, background: colors.borderDefault }} />

                {/* Calls/day */}
                <div className="flex-1 flex flex-col justify-start">
                  <p className="text-[2rem] font-bold font-mono tabular-nums leading-none" style={{ color: colors.textPrimary }}>
                    {fmtNum(summary.totalCallsPerDay)}
                  </p>
                  <p className="text-xs font-mono mt-1.5" style={{ color: colors.textMuted }}>calls/day</p>
                </div>

                <div className="mx-5 self-stretch" style={{ width: 1, background: colors.borderDefault }} />

                {/* High risk */}
                <div className="flex-1 flex flex-col justify-start">
                  <p className="text-[2rem] font-bold font-mono tabular-nums leading-none" style={{ color: summary.highRiskCount > 0 ? accent : colors.textPrimary }}>
                    {summary.highRiskCount}
                  </p>
                  <p className="text-xs font-mono mt-1.5" style={{ color: summary.highRiskCount > 0 ? accent : colors.textMuted }}>high risk</p>
                  {riskBreakdown.length > 0 && (
                    <div className="flex flex-wrap items-center gap-y-0.5 mt-1.5">
                      {riskBreakdown.map((r, i) => (
                        <span key={r.label} className="text-[10px] font-mono" style={{ color: accent }}>
                          {i > 0 && <span className="mx-1" style={{ color: colors.borderDefault }}>·</span>}
                          {r.count} {r.label}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            </>
          )}

          {/* Top Cost Driver — inside left column */}
          {topEndpoint && !endpointsLoading && (
            <div
              className="rounded-lg px-5 py-4"
              style={{
                background: colors.bgBase,
                border: `1px solid ${colors.borderDefault}`,
              }}
            >
              <p className="text-[10px] uppercase tracking-[0.12em] font-semibold mb-3" style={{ color: colors.textMuted }}>
                Top cost driver
              </p>
              <div className="flex items-start gap-3 min-w-0">
                <Badge color={METHOD_STYLES[topEndpoint.method] ?? DEFAULT_METHOD_STYLE} className="flex-shrink-0 justify-center w-[46px] text-[10px]">
                  {topEndpoint.method}
                </Badge>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-xs font-medium" style={{ color: colors.textMuted }}>
                      {topEndpoint.provider}
                    </span>
                    <span
                      className="text-sm font-mono truncate"
                      style={{ color: colors.textPrimary }}
                      title={topEndpoint.url}
                    >
                      {topEndpoint.url.replace(/^https?:\/\/[^/]+/, '')}
                    </span>
                  </div>
                  {topEndpointSuggestion && (
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <TrendingDown className="w-3 h-3 flex-shrink-0" style={{ color: accent }} />
                      <span className="text-xs font-mono" style={{ color: accent }}>
                        Switch to {SUGGESTION_TYPE_LABELS[topEndpointSuggestion.type]}
                        {topEndpointSuggestion.estimatedMonthlySavings > 0
                          ? ` — save ~${fmtCost(topEndpointSuggestion.estimatedMonthlySavings)}/mo`
                          : ''}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-shrink-0 text-right ml-2">
                  <p
                    className="text-lg font-mono font-semibold tabular-nums"
                    style={{ color: topEndpoint.monthlyCost > 0 ? accent : colors.textMuted }}
                  >
                    {fmtCost(topEndpoint.monthlyCost)}<span className="text-[10px] font-normal ml-0.5">/mo</span>
                  </p>
                  <p className="text-[10px] font-mono mt-0.5" style={{ color: colors.textMuted }}>
                    {fmtNum(topEndpoint.callsPerDay)}/day
                  </p>
                </div>
              </div>
            </div>
          )}

          </div>{/* end left column */}

          {/* Right column — Cost by Provider (wraps content, spans full height) */}
          {providerGroups.some((g) => g.totalCost > 0) && grandTotal > 0 && (
            <div
              className="flex flex-col rounded-lg p-5 sm:p-6"
              style={{
                flex: '0 0 370px',
                background: colors.bgBase,
                border: `1px solid ${colors.borderDefault}`,
              }}
            >
              <p className="text-[10px] uppercase tracking-[0.12em] mb-3" style={{ color: colors.textMuted }}>
                Cost by Provider
              </p>
              <div className="flex flex-col items-center justify-center flex-1">
                <div style={{ width: 200, height: 200 }}>
                  <PieChart width={200} height={200}>
                    <Pie
                      data={providerGroups.filter((g) => g.totalCost > 0).map((g) => ({
                        name: g.provider,
                        value: g.totalCost,
                      }))}
                      cx={100}
                      cy={100}
                      innerRadius={60}
                      outerRadius={86}
                      dataKey="value"
                      strokeWidth={0}
                    >
                      {providerGroups.filter((g) => g.totalCost > 0).map((g) => (
                        <Cell key={g.provider} fill={PROVIDER_COLORS[g.provider.toLowerCase()] ?? colors.textSecondary} />
                      ))}
                    </Pie>
                  </PieChart>
                </div>
                <div className="flex flex-wrap justify-center gap-x-3 gap-y-1.5 mt-3" style={{ maxWidth: 200 }}>
                  {providerGroups.filter((g) => g.totalCost > 0).slice(0, 6).map((g) => {
                    const c = PROVIDER_COLORS[g.provider.toLowerCase()] ?? colors.textSecondary;
                    return (
                      <div key={g.provider} className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: c }} />
                        <span className="text-[11px] font-mono" style={{ color: colors.textSecondary }}>{g.provider}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

        </Motion.div>

        {/* ── 3. Provider breakdown ─────────────────────────────────────────── */}
        <Motion.div {...FADE(0)} className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.12em] mb-1" style={{ color: colors.textMuted }}>
                {latestScan?.created_at || latestScan?.createdAt
                  ? `Latest scan · ${fmt(latestScan.created_at ?? latestScan.createdAt)}`
                  : 'Latest scan'}
              </p>
              <h2 className="text-lg font-bold" style={{ color: colors.textPrimary }}>
                Provider breakdown
                {endpoints.length > 0 && (
                  <span className="ml-2 text-sm font-mono font-normal" style={{ color: colors.textMuted }}>
                    {endpoints.length}
                  </span>
                )}
              </h2>
            </div>
          </div>

          {endpointsLoading ? (
            <CenteredSpinner />
          ) : endpointsError ? (
            <ErrorState message="Failed to load endpoints" onRetry={() => refetchEndpoints()} />
          ) : providerGroups.length === 0 ? (
            <Card
              className="flex flex-col items-center justify-center py-16 text-center"
            >
              <Layers className="w-6 h-6 mb-3" style={{ color: colors.textMuted }} />
              <p className="text-sm font-semibold mb-1" style={{ color: colors.textPrimary }}>No endpoints</p>
              <p className="text-xs" style={{ color: colors.textMuted }}>
                {latestScan ? 'No endpoints found in this scan.' : 'Submit a scan via the API or extension to see results.'}
              </p>
            </Card>
          ) : (
            <div
              className="rounded-lg overflow-hidden"
              style={{ border: `1px solid ${colors.borderDefault}`, background: colors.bgBase }}
            >
              {providerGroups.map((group) => (
                <ProviderGroup
                  key={group.provider}
                  provider={group.provider}
                  endpoints={group.endpoints}
                  totalCost={group.totalCost}
                  grandTotal={grandTotal}
                  suggestions={suggestionsLoading ? [] : suggestions}
                />
              ))}
            </div>
          )}
          {hasMoreEndpoints && (
            <div className="mt-4 flex justify-center">
              <button
                onClick={() => fetchMoreEndpoints()}
                disabled={isFetchingMoreEndpoints}
                className="px-4 py-2 rounded-md text-sm transition-colors disabled:opacity-50"
                style={{ border: `1px solid ${colors.borderDefault}`, color: colors.textSecondary, background: colors.bgBase }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = colors.borderHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = colors.borderDefault; }}
              >
                {isFetchingMoreEndpoints ? 'Loading…' : 'Load more endpoints'}
              </button>
            </div>
          )}
        </Motion.div>

        {/* ── 4. Scanned files ──────────────────────────────────────────────── */}
        {!endpointsLoading && endpoints.length > 0 && (
          <ScannedFiles endpoints={endpoints} />
        )}

        {/* ── 5. Scan history ───────────────────────────────────────────────── */}
        <Motion.div {...FADE(0)} data-onboarding-target="scan-history">
          <button
            className="flex items-center gap-2 mb-4 cursor-pointer focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)] rounded"
            style={{ background: 'none', border: 'none', padding: 0 }}
            onClick={() => setHistoryOpen((v) => !v)}
          >
            <p className="text-xs uppercase tracking-[0.12em]" style={{ color: colors.textMuted }}>Scan history</p>
            {historyOpen
              ? <ChevronUp className="w-3.5 h-3.5" style={{ color: colors.textMuted }} />
              : <ChevronDown className="w-3.5 h-3.5" style={{ color: colors.textMuted }} />}
          </button>

          {historyOpen && (
            scansLoading ? (
              <CenteredSpinner />
            ) : scansError ? (
              <ErrorState message="Failed to load scan history" onRetry={() => refetchScans()} />
            ) : scans.length === 0 ? (
              <Card
                className="flex flex-col items-center justify-center py-14"
              >
                <p className="text-sm" style={{ color: colors.textMuted }}>No scans yet.</p>
              </Card>
            ) : (
              <Card
                className="overflow-hidden"
              >
                {scans.map((scan, i) => (
                  <div
                    key={scan.id}
                    className="flex items-center justify-between px-3 py-3 sm:px-5"
                    style={{ borderBottom: i < scans.length - 1 ? `1px solid ${colors.borderSubtle}` : undefined }}
                  >
                    <div className="flex items-center gap-3">
                      <FolderKanban className="w-3.5 h-3.5 flex-shrink-0" style={{ color: colors.textMuted }} />
                      <div>
                        <p className="text-sm font-semibold" style={{ color: colors.textPrimary }}>
                          Scan #{scans.length - i}
                        </p>
                        <p className="text-xs font-mono" style={{ color: colors.textMuted }}>
                          {fmt(scan.created_at ?? scan.createdAt)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-xs font-mono tabular-nums" style={{ color: colors.textSecondary }}>
                        {scan.summary?.totalEndpoints ?? 0} endpoints
                      </span>
                      <span className="text-sm font-mono font-semibold tabular-nums" style={{ color: scan.summary?.totalMonthlyCost > 0 ? accent : colors.textMuted }}>
                        {fmtCost(scan.summary?.totalMonthlyCost ?? 0)}
                      </span>
                      {scan.summary?.highRiskCount > 0 && (
                        <div
                          className="flex items-center gap-1 px-2 py-0.5 rounded"
                          style={{ background: colors.errorSubtle, border: `1px solid ${colors.errorBorder}` }}
                        >
                          <AlertTriangle className="w-3 h-3" style={{ color: colors.error }} />
                          <span className="text-[10px] font-mono" style={{ color: colors.error }}>
                            {scan.summary.highRiskCount}
                          </span>
                        </div>
                      )}
                      <button
                        onClick={() => deleteScanMutation.mutate(scan.id)}
                        disabled={deleteScanMutation.isPending}
                        aria-label="Delete scan"
                        className="flex items-center justify-center w-6 h-6 rounded transition-colors disabled:opacity-40 focus-visible:outline-none focus-visible:shadow-[0_0_0_1.5px_rgba(242,107,29,0.5)]"
                        style={{ color: colors.textMuted, background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = colors.textPrimary)}
                        onMouseLeave={(e) => (e.currentTarget.style.color = colors.textMuted)}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
                {scansPagination && scansPagination.totalPages > 1 && (
                  <div
                    className="flex items-center justify-between px-3 py-2 sm:px-5"
                    style={{ borderTop: `1px solid ${colors.borderSubtle}` }}
                  >
                    <button
                      disabled={!scansPagination.hasPrev}
                      onClick={() => setScansPage(p => p - 1)}
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      style={{ color: colors.textSecondary, border: `1px solid ${colors.borderDefault}` }}
                    >
                      <ChevronUp className="w-3 h-3 rotate-[-90deg]" />
                      Prev
                    </button>
                    <span className="text-xs font-mono" style={{ color: colors.textMuted }}>
                      Page {scansPagination.page} of {scansPagination.totalPages}
                    </span>
                    <button
                      disabled={!scansPagination.hasNext}
                      onClick={() => setScansPage(p => p + 1)}
                      className="flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      style={{ color: colors.textSecondary, border: `1px solid ${colors.borderDefault}` }}
                    >
                      Next
                      <ChevronDown className="w-3 h-3 rotate-[-90deg]" />
                    </button>
                  </div>
                )}
              </Card>
            )
          )}
        </Motion.div>
        </>}

        {/* Analytics tab */}
        {activeTab === 'analytics' && <AnalyticsTab projectId={id} />}

        {/* Settings tab */}
        {activeTab === 'settings' && (
          <SettingsTab projectId={id} project={project ?? null} />
        )}

      </div>

      {onboarding.shouldShowProjectTour ? (
        <OnboardingSpotlight
          selector={onboardingStep.selector}
          title={onboardingStep.title}
          body={onboardingStep.body}
          stepLabel={onboardingStep.stepLabel}
          noOverlay={'noOverlay' in onboardingStep && onboardingStep.noOverlay === true}
          highlightScale={onboardingStepIndex === 1 ? 1.15 : 1}
          onSkip={() => onboarding.completeTour()}
          backAction={
            onboardingStepIndex > 0
              ? { label: 'Back', onClick: () => onboarding.setTourStep(onboardingStepIndex - 1) }
              : undefined
          }
          primaryAction={
            onboardingStepIndex === ONBOARDING_TOUR_STEPS.length - 1
              ? {
                  label: 'Generate API key',
                  onClick: () => {
                    onboarding.completeTour();
                    navigate('/dashboard/account');
                  },
                }
              : {
                  label: 'Next',
                  onClick: () => onboarding.setTourStep(onboardingStepIndex + 1),
                }
          }
          secondaryAction={
            onboardingStepIndex === ONBOARDING_TOUR_STEPS.length - 1
              ? {
                  label: 'Explore demo data',
                  onClick: () => onboarding.completeTour(),
                }
              : undefined
          }
        />
      ) : null}
    </div>
  );
}

